import { create } from "zustand";
import isEqual from "lodash/isEqual";
import { isAndroid, isIOS } from "react-device-detect";

import {
  DeckObject,
  KnowledgeUnderstandingRating,
  OcclusionImageWithBoxPositions,
  PageSetupProps,
  CardData,
  SignUpAndOnboardingModalActivatedReason,
  SignUpAndOnboardingModalView,
  KeyConcept,
  CardType,
  LearningJourneyModule,
  SummaryOutlineObject,
  UserInteractionForPageGeneration,
  UnderstandingRatingsForNonAuthenticatedUser,
  FreeResponseAnswerFromUserWithFeedback,
  PDFImage,
  SpacedRepetitionReviewData,
  ContentMediumType,
  ChatMessage,
  StartStudyingDeckReviewAlgorithmType,
  TimeFilterNameToUseForLeaderboard,
  PriceDetails,
  ChatSidePanelProps,
  SidePanelLocation,
  UserSubscriptionPlan,
  ReasonForShowingSubscriptionUpsell,
  ExamObject,
  MIN_WIDTH_OF_TABLET_PORTAIT_VIEW,
  MAX_WIDTH_OF_TABLET_PORTAIT_VIEW,
  Tree,
  GooglePlaySubscriptionProductDetails,
  AppleSubscriptionProductDetails,
  FileUploadWithDeckData,
  UserData,
  LeaderboardData,
  GroupQuestionTypeBy,
  AssessmentUnitStimulusBlock,
} from "../constants/GlobalTypesAndEnumsAndConstants";
import {
  orderCardsByCaseScenario,
  orderCardsToHaveTheFirstFourBeMultipleChoiceQuestionsAndGroupCaseQuestions,
} from "../backend/utils/helper-methods";
import { NextRewardName } from "../backend/utils/gamification-helper-methods/progression-and-river-animation";
import { getQuestionVersionToDisplay, updateCardWithVersionBeingUsed } from "../backend/utils/rephrasing-helpers";
import { startPaywallJourneyReplay } from "../utils/analytics/paywallReplayLifecycle";
import type {
  PaywallFormatStratum,
  PaywallFormatVariant,
  UpsellFlowVariant,
} from "../components/upsell/upsellFlowConfig";
import { orderCardsForReviewByAssessmentUnit } from "../utils/assessment-units";
import { getReviewIndexAfterRemovingCards } from "../utils/remove-write-in-questions";
import { removePdfImageFromGenerationDocuments, updatePdfImageInGenerationDocuments } from "./pdf-image-state";
import { useReviewCheckpointChallengeStore } from "./reviewCheckpointChallengeStore";

// ***** CONCEPT STORE ***** //
// ***** CONCEPT STORE ***** //
type ConceptStoreState = {
  isFreeRecallTextareaFocused: boolean;
  setIsFreeRecallTextareaFocused: (value: boolean) => void;

  currentConceptIndex: number;
  setCurrentConceptIndex: (value: number) => void;
  nextKeyConceptFreeRecall: () => void;
  previousKeyConceptFreeRecall: () => void;
};

export const conceptStore = create<ConceptStoreState>((set) => ({
  isFreeRecallTextareaFocused: false,
  setIsFreeRecallTextareaFocused: (value: boolean) => set({ isFreeRecallTextareaFocused: value }),

  currentConceptIndex: 0,
  setCurrentConceptIndex: (value: number) => set({ currentConceptIndex: value }),
  nextKeyConceptFreeRecall: () => set((state) => ({ currentConceptIndex: state.currentConceptIndex + 1 })),
  previousKeyConceptFreeRecall: () => set((state) => ({ currentConceptIndex: state.currentConceptIndex - 1 })),
}));

// ***** PAGE SETUP ***** //
// ***** PAGE SETUP ***** //
type PageStoreState = {
  pageSetup: PageSetupProps | null;
  setPageSetup: (value: PageSetupProps) => void;

  isHeaderFixed: boolean;
  setIsHeaderFixed: (value: boolean) => void;

  shouldShowChromeExtensionUpsell: boolean;
  setShouldShowChromeExtensionUpsell: (value: boolean) => void;
  isRunningAsMobileApp: boolean;
  setIsRunningAsMobileApp: (value: boolean) => void;

  mobileBackButtonAction: () => void;
  setMobileBackButtonAction: (value: () => void) => void;

  priceDetails: PriceDetails | null;
  setPriceDetails: (value: PriceDetails) => void;

  googlePlaySubscriptionPriceDetails: GooglePlaySubscriptionProductDetails | null;
  setGooglePlaySubscriptionPriceDetails: (value: GooglePlaySubscriptionProductDetails) => void;

  appleSubscriptionPriceDetails: AppleSubscriptionProductDetails[] | null;
  setAppleSubscriptionPriceDetails: (value: AppleSubscriptionProductDetails[]) => void;

  isIOS: boolean;
  setIsIOS: (value: boolean) => void;
  isAndroid: boolean;
  setIsAndroid: (value: boolean) => void;

  currentCountryUserIsIn: string | null;
  setCurrentCountryUserIsIn: (value: string | null) => void;

  iosVersionPublished: number | 0;
  setIosVersionPublished: (value: number | null) => void;

  androidVersionPublished: number | 0;
  setAndroidVersionPublished: (value: number | null) => void;

  hasTrackedMainHomePageVisit: boolean;
  setHasTrackedMainHomePageVisit: (value: boolean) => void;
};

export const pageStore = create<PageStoreState>((set) => ({
  pageSetup: {
    headerType: null,
    showJungleBackground: false,
  },
  setPageSetup: (value: PageSetupProps) => set({ pageSetup: value }),

  isHeaderFixed: false,
  setIsHeaderFixed: (value: boolean) => set({ isHeaderFixed: value }),

  shouldShowChromeExtensionUpsell: false,
  setShouldShowChromeExtensionUpsell: (value: boolean) => set({ shouldShowChromeExtensionUpsell: value }),
  isRunningAsMobileApp: false,
  setIsRunningAsMobileApp: (value: boolean) => set({ isRunningAsMobileApp: value }),

  mobileBackButtonAction: null,
  setMobileBackButtonAction: (value: () => void) => set({ mobileBackButtonAction: value }),

  priceDetails: null,
  setPriceDetails: (value: PriceDetails) => set({ priceDetails: value }),

  googlePlaySubscriptionPriceDetails: null,
  setGooglePlaySubscriptionPriceDetails: (value: GooglePlaySubscriptionProductDetails) =>
    set({ googlePlaySubscriptionPriceDetails: value }),

  appleSubscriptionPriceDetails: null,
  setAppleSubscriptionPriceDetails: (value: AppleSubscriptionProductDetails[]) =>
    set({ appleSubscriptionPriceDetails: value }),

  isIOS: isIOS,
  setIsIOS: (value: boolean) => set({ isIOS: value }),
  isAndroid: isAndroid,
  setIsAndroid: (value: boolean) => set({ isAndroid: value }),

  currentCountryUserIsIn: null,
  setCurrentCountryUserIsIn: (value: string | null) => set({ currentCountryUserIsIn: value }),

  iosVersionPublished: 0,
  setIosVersionPublished: (value: number | null) => set({ iosVersionPublished: value }),

  androidVersionPublished: 0,
  setAndroidVersionPublished: (value: number | null) => set({ androidVersionPublished: value }),

  hasTrackedMainHomePageVisit: false,
  setHasTrackedMainHomePageVisit: (value: boolean) => set({ hasTrackedMainHomePageVisit: value }),
}));

// ***** SIGN UP AND ONBOARDING MODAL ***** //
// ***** SIGN UP AND ONBOARDING MODAL ***** //
type SignUpAndOnboardingModalStoreType = {
  shouldShowModalSignUpAndLogin: boolean | null;
  currentModalViewBeingShown: SignUpAndOnboardingModalView | null;
  modalActivatedReason: SignUpAndOnboardingModalActivatedReason | null;

  setShowModalSignUpAndLogin: (
    currentModalViewBeingShown: SignUpAndOnboardingModalView | null,
    modalActivatedReason?: SignUpAndOnboardingModalActivatedReason | null
  ) => void;
  setHideModalSignUpAndLogin: () => void;
  setCurrentModalViewBeingShown: (value: SignUpAndOnboardingModalView) => void;
};

export const signUpAndOnboardingModalStore = create<SignUpAndOnboardingModalStoreType>((set) => ({
  shouldShowModalSignUpAndLogin: false,
  currentModalViewBeingShown: null,
  modalActivatedReason: null,
  setShowModalSignUpAndLogin: (
    currentModalViewBeingShown: SignUpAndOnboardingModalView | null,
    modalActivatedReason?: SignUpAndOnboardingModalActivatedReason | null
  ) => {
    // Set state in the current store
    set({
      shouldShowModalSignUpAndLogin: true,
      currentModalViewBeingShown: currentModalViewBeingShown,
      modalActivatedReason: modalActivatedReason,
    });
    // Set state in the global website store
    useGlobalWebsiteStore.getState().setIsAnyModalOpen(true);
  },
  setCurrentModalViewBeingShown: (value: SignUpAndOnboardingModalView) => set({ currentModalViewBeingShown: value }),
  setHideModalSignUpAndLogin: () => {
    set({ shouldShowModalSignUpAndLogin: false, modalActivatedReason: null, currentModalViewBeingShown: null });
    useGlobalWebsiteStore.getState().setIsAnyModalOpen(false);
  },
}));

type ShowAllCardsState = {
  shouldShowAllCardsInDeckAfterCreatingAccount: boolean;
  setShouldShowAllCardsInDeckAfterCreatingAccount: (value: boolean) => void;
};

export const showAllCardsInDeckAfterCreatingAccountStore = create<ShowAllCardsState>((set) => ({
  shouldShowAllCardsInDeckAfterCreatingAccount: false,
  setShouldShowAllCardsInDeckAfterCreatingAccount: (value: boolean) =>
    set({ shouldShowAllCardsInDeckAfterCreatingAccount: value }),
}));

// ***** REVIEW CARDS ***** //
// ***** REVIEW CARDS ***** //
type ReviewDeckStoreState = {
  isShowingCardAnswers: boolean;
  setIsShowingCardAnswers: (value: boolean) => void;

  groupQuestionTypeBy: GroupQuestionTypeBy;
  setGroupQuestionTypeBy: (value: GroupQuestionTypeBy) => void;

  setShouldShowGratitudeWallScreen: (value: boolean) => void;
  shouldShowGratitudeWallScreen: boolean;

  shouldShowQuestionPruningScreen: boolean;
  setShouldShowQuestionPruningScreen: (value: boolean) => void;

  numOfConsecutiveCorrectAnswers: number;
  setNumOfConsecutiveCorrectAnswers: (value: number) => void;

  maxDeckProgressScore: number;
  setMaxDeckProgressScore: (value: number) => void;

  currentDeckProgressScore: number;
  setCurrentDeckProgressScore: (value: number) => void;

  // Drives the review progress bar's "new progress" emphasis: after a CORRECT
  // answer, the segment from `startScore` (the deck score before this answer)
  // to the new score glows brighter. `cardId` ties it to the answered card so
  // the emphasis clears automatically when the user moves to another question.
  // Null = no emphasis (default, or after an incorrect answer).
  progressDeltaEmphasis: { cardId: string; startScore: number } | null;
  setProgressDeltaEmphasis: (value: { cardId: string; startScore: number } | null) => void;

  lastDeckProgressScoreShowedOnLastPointOfControl: number;
  setLastDeckProgressScoreShowedOnLastPointOfControl: (value: number) => void;

  isUserDoingDailyReview: boolean;
  setisUserDoingDailyReview: (value: boolean) => void;

  startStudyingDeckReviewAlgorithmType: StartStudyingDeckReviewAlgorithmType;
  studyModeSelectedByUserId: string | undefined;
  setStartStudyingDeckReviewAlgorithmType: (
    value: StartStudyingDeckReviewAlgorithmType,
    selectedByUserId?: string | null
  ) => void;

  generationsHistoryListGroupedByMonth: {
    month: string;
    generated_decks?: DeckObject[];
  }[];

  setGenerationsHistoryListGroupedByMonth: (
    value: {
      month: string;
      generated_decks?: DeckObject[];
    }[]
  ) => void;

  currentReviewClass: DeckObject | null;
  setCurrentReviewClass: (value: DeckObject) => void;

  currentReviewDeck: DeckObject | null;
  setCurrentReviewDeck: (value: DeckObject | null) => void;

  isDeckPageLoading: boolean;
  setIsDeckPageLoading: (value: boolean) => void;

  isShowingSelectDeckModal: boolean | null;
  setIsShowingSelectDeckModal: (value: boolean) => void;

  sharedDeck: DeckObject | null; // this is the deck that is shared with the user
  setSharedDeck: (value: DeckObject) => void;

  summaryOutlinesForDeckAndSubdecks: SummaryOutlineObject[] | null;
  setSummaryOutlinesForDeckAndSubdecks: (summaryOutlines: SummaryOutlineObject[] | null) => void;

  ankiDecks: any[];
  setAnkiDecks: (value: any[]) => void;

  shouldShowDeckOverviewForNonAuthenticatedUserPage: boolean | null;
  setShouldShowDeckOverviewForNonAuthenticatedUserPage: (value: boolean) => void;

  deckToGenerateFor: DeckObject | null;
  setDeckToGenerateForCards: (value: DeckObject) => void;

  addImageForPdfImages: (value: PDFImage) => void;
  updateImageForCurrentGenerationDocument: (value: PDFImage) => void;
  removeImageFromCurrentGenerationDocument: (imageUrl: string) => void;

  shouldBeSharedDeckDuplicated: boolean;
  setShouldBeSharedDeckDuplicated: (value: boolean) => void;

  // Moved from DeckState
  userLatestThreeDecksData: DeckObject[];
  setUserLatestThreeDecksData: (value: DeckObject[]) => void;

  documentsAndUrlsForCardGenerationArray: FileUploadWithDeckData[] | null; // This is used to store the PDF files that are uploaded by the user to generate cards
  setDocumentsAndUrlsForCardGenerationArray: (files: FileUploadWithDeckData[] | null) => void;
  updateAssociatedDeckForCurrentDocumentOrUrl: (deck: DeckObject) => void;

  currentInputIndexInDocumentsAndUrlsArray: number | null;
  setCurrentInputIndexInDocumentsAndUrlsArray: (value: number | null) => void;

  // Stores the URL to navigate to after exiting a review session.
  // When set (e.g. "/folder/abc123"), the exit button navigates there instead of "/".
  // When null, the exit button navigates to home ("/").
  navigationDestinationAfterExitingReview: string | null;
  setNavigationDestinationAfterExitingReview: (value: string | null) => void;

  // An anonymous review action stays on the folder behind the sign-up modal.
  // Once authentication and onboarding finish, AppRouter consumes this exact
  // destination so a shared deck is reviewed in place rather than duplicated.
  pendingReviewDestinationAfterAuthentication: string | null;
  setPendingReviewDestinationAfterAuthentication: (value: string | null) => void;
};

export const useReviewDeckStore = create<ReviewDeckStoreState>((set) => ({
  groupQuestionTypeBy: GroupQuestionTypeBy.QuestionType,
  setGroupQuestionTypeBy: (value: GroupQuestionTypeBy) => set({ groupQuestionTypeBy: value }),

  isShowingCardAnswers: false,
  setIsShowingCardAnswers: (value: boolean) => set({ isShowingCardAnswers: value }),

  shouldShowGratitudeWallScreen: false,
  setShouldShowGratitudeWallScreen: (value: boolean) => set({ shouldShowGratitudeWallScreen: value }),

  shouldShowQuestionPruningScreen: false,
  setShouldShowQuestionPruningScreen: (value: boolean) => set({ shouldShowQuestionPruningScreen: value }),

  numOfConsecutiveCorrectAnswers: 0,
  setNumOfConsecutiveCorrectAnswers: (value: number) => set({ numOfConsecutiveCorrectAnswers: value }),

  maxDeckProgressScore: 1, // when we load the deckProgressScore ratio, it can be currentDeckProgressScore/maxDeckProgressScore = 0 instead of infinity
  setMaxDeckProgressScore: (value: number) => set({ maxDeckProgressScore: value }),

  currentDeckProgressScore: null,
  setCurrentDeckProgressScore: (value: number) => set({ currentDeckProgressScore: value }),

  progressDeltaEmphasis: null,
  setProgressDeltaEmphasis: (value) => set({ progressDeltaEmphasis: value }),

  lastDeckProgressScoreShowedOnLastPointOfControl: null,
  setLastDeckProgressScoreShowedOnLastPointOfControl: (value: number) =>
    set({ lastDeckProgressScoreShowedOnLastPointOfControl: value }),

  startStudyingDeckReviewAlgorithmType: StartStudyingDeckReviewAlgorithmType.RESUME_WHERE_I_LEFT_OFF,
  studyModeSelectedByUserId: undefined,
  setStartStudyingDeckReviewAlgorithmType: (
    value: StartStudyingDeckReviewAlgorithmType,
    selectedByUserId: string | null = userStore.getState().currentUser?.id ?? null
  ) => {
    if (selectedByUserId !== (userStore.getState().currentUser?.id ?? null)) return;
    set({
      startStudyingDeckReviewAlgorithmType: value,
      studyModeSelectedByUserId: selectedByUserId ?? undefined,
    });
  },

  isUserDoingDailyReview: false,
  setisUserDoingDailyReview: (value: boolean) => set({ isUserDoingDailyReview: value }),

  generationsHistoryListGroupedByMonth: [],
  setGenerationsHistoryListGroupedByMonth: (value: { month: string; generated_decks?: DeckObject[] }[]) =>
    set({ generationsHistoryListGroupedByMonth: value }),

  currentReviewClass: null,
  setCurrentReviewClass: (value: DeckObject) => set({ currentReviewClass: value }),

  currentReviewDeck: null,
  setCurrentReviewDeck: (updatedDeck: DeckObject | null) => {
    set((state) => {
      // If updatedDeck is null, only set to null if explicitly intended
      if (!updatedDeck) {
        return { currentReviewDeck: updatedDeck };
      }

      // If we don't have a current deck or the IDs don't match, replace entirely
      if (!state.currentReviewDeck || state.currentReviewDeck.id !== updatedDeck.id) {
        return { currentReviewDeck: updatedDeck };
      }

      // If IDs match, merge the deck to preserve the existing state
      const mergedDeck = {
        ...state.currentReviewDeck,
        ...updatedDeck,
        // Preserve nested objects by merging them separately
        generation_metadata: {
          ...state.currentReviewDeck.generation_metadata,
          ...(updatedDeck.generation_metadata || {}),
        },
      };

      return { currentReviewDeck: mergedDeck };
    });
  },

  isDeckPageLoading: false,
  setIsDeckPageLoading: (value: boolean) => set({ isDeckPageLoading: value }),

  isShowingSelectDeckModal: false,
  setIsShowingSelectDeckModal: (value: boolean) => set({ isShowingSelectDeckModal: value }),

  sharedDeck: null,
  setSharedDeck: (value: DeckObject) => set({ sharedDeck: value }),

  summaryOutlinesForDeckAndSubdecks: null,
  setSummaryOutlinesForDeckAndSubdecks: (summaryOutlines: SummaryOutlineObject[] | null) =>
    set({ summaryOutlinesForDeckAndSubdecks: summaryOutlines }),

  ankiDecks: [],
  setAnkiDecks: (value: DeckObject[]) => set({ ankiDecks: value }),

  shouldShowDeckOverviewForNonAuthenticatedUserPage: false,
  setShouldShowDeckOverviewForNonAuthenticatedUserPage: (value: boolean) =>
    set({ shouldShowDeckOverviewForNonAuthenticatedUserPage: value }),

  deckToGenerateFor: null,
  setDeckToGenerateForCards: (value: DeckObject) => set({ deckToGenerateFor: value }),
  addImageForPdfImages: (value: PDFImage) => {
    set((state) => {
      let currentDeck =
        state.documentsAndUrlsForCardGenerationArray?.[state.currentInputIndexInDocumentsAndUrlsArray || 0]
          ?.associatedDeck;
      let currentPdfImagesObjectList = currentDeck?.generation_metadata?.pdf_images_object_list || [];
      let isImageDuplicate = currentPdfImagesObjectList.some((imageObject) => imageObject.imageUrl === value.imageUrl);

      if (!isImageDuplicate) {
        currentPdfImagesObjectList.push(value);
      }
      currentDeck.generation_metadata.pdf_images_object_list = currentPdfImagesObjectList;
      state.documentsAndUrlsForCardGenerationArray[state.currentInputIndexInDocumentsAndUrlsArray || 0].associatedDeck =
        currentDeck;
      return { ...state };
    });
  },
  updateImageForCurrentGenerationDocument: (value: PDFImage) => {
    set((state) => {
      const documentsAndUrlsForCardGenerationArray = updatePdfImageInGenerationDocuments(
        state.documentsAndUrlsForCardGenerationArray,
        state.currentInputIndexInDocumentsAndUrlsArray,
        value
      );
      if (documentsAndUrlsForCardGenerationArray === state.documentsAndUrlsForCardGenerationArray) return {};
      return { documentsAndUrlsForCardGenerationArray };
    });
  },
  removeImageFromCurrentGenerationDocument: (imageUrl: string) => {
    set((state) => {
      const documentsAndUrlsForCardGenerationArray = removePdfImageFromGenerationDocuments(
        state.documentsAndUrlsForCardGenerationArray,
        state.currentInputIndexInDocumentsAndUrlsArray,
        imageUrl
      );
      if (documentsAndUrlsForCardGenerationArray === state.documentsAndUrlsForCardGenerationArray) return {};
      return { documentsAndUrlsForCardGenerationArray };
    });
  },

  // Used to duplicate the deck when it is shared by another user
  shouldBeSharedDeckDuplicated: false,
  setShouldBeSharedDeckDuplicated: (value: boolean) => set({ shouldBeSharedDeckDuplicated: value }),

  // Moved from DeckState
  userLatestThreeDecksData: [],
  setUserLatestThreeDecksData: (value: DeckObject[]) => set({ userLatestThreeDecksData: value }),

  documentsAndUrlsForCardGenerationArray: null,
  setDocumentsAndUrlsForCardGenerationArray: (files: FileUploadWithDeckData[] | null) =>
    set({ documentsAndUrlsForCardGenerationArray: files }),
  updateAssociatedDeckForCurrentDocumentOrUrl: (deck: DeckObject) => {
    set((state) => {
      if (!state.documentsAndUrlsForCardGenerationArray) return {};
      const updatedFiles = state.documentsAndUrlsForCardGenerationArray.map((file, index) => {
        if (index === state.currentInputIndexInDocumentsAndUrlsArray) {
          return { ...file, deck: deck };
        }
        return file;
      });
      return { documentsAndUrlsForCardGenerationArray: updatedFiles };
    });
  },

  currentInputIndexInDocumentsAndUrlsArray: null,
  setCurrentInputIndexInDocumentsAndUrlsArray: (value: number | null) =>
    set({ currentInputIndexInDocumentsAndUrlsArray: value }),

  navigationDestinationAfterExitingReview: null,
  setNavigationDestinationAfterExitingReview: (value: string | null) =>
    set({ navigationDestinationAfterExitingReview: value }),

  pendingReviewDestinationAfterAuthentication: null,
  setPendingReviewDestinationAfterAuthentication: (value: string | null) =>
    set({ pendingReviewDestinationAfterAuthentication: value }),
}));

// ***** GENERATE CARDS OPTIONS ***** //
// ***** GENERATE CARDS OPTIONS ***** //
type GenerateCardsStoreType = {
  shouldShowSelectImagesForOcclusionView: boolean;
  setShouldShowSelectImagesForOcclusionView: (value: boolean) => void;

  selectedOcclusionImagesWithBoxPositions: Array<OcclusionImageWithBoxPositions> | null;
  setSelectedOcclusionImagesWithBoxPositions: (value: OcclusionImageWithBoxPositions[] | null) => void;

  lastPageTextGenerated: string | null;
  setLastPageTextGenerated: (value: string) => void;

  fileUploadErrorMessage: string | null;
  setFileUploadErrorMessage: (value: string | null) => void;

  // True while the first-time home is on screen. It shows an upload's error under the upload that failed, so the
  // router leaves the learner there instead of opening the upload page
  isFirstTimeHomeOnScreen: boolean;
  setIsFirstTimeHomeOnScreen: (value: boolean) => void;

  // used for displaying correct text on the loading screen
  currentContentMediumType: ContentMediumType | null;
  setCurrentContentMediumType: (value: ContentMediumType) => void;

  isGeneratingYoutubeVideoTranscript: boolean | null;
  setIsGeneratingYoutubeVideoTranscript: (value: boolean) => void;
};

export const generateCardsStore = create<GenerateCardsStoreType>((set) => ({
  shouldShowSelectImagesForOcclusionView: false,
  setShouldShowSelectImagesForOcclusionView: (value: boolean) => set({ shouldShowSelectImagesForOcclusionView: value }),

  selectedOcclusionImagesWithBoxPositions: null,
  setSelectedOcclusionImagesWithBoxPositions: (value: OcclusionImageWithBoxPositions[]) =>
    set({ selectedOcclusionImagesWithBoxPositions: value }),

  lastPageTextGenerated: null,
  setLastPageTextGenerated: (value: string) => set({ lastPageTextGenerated: value }),

  fileUploadErrorMessage: null,
  setFileUploadErrorMessage: (value: string | null) => set({ fileUploadErrorMessage: value }),

  isFirstTimeHomeOnScreen: false,
  // Every home visit sets it; only a change may notify, or the router and every page using this store re-render
  setIsFirstTimeHomeOnScreen: (value: boolean) =>
    set((state) => (state.isFirstTimeHomeOnScreen === value ? state : { isFirstTimeHomeOnScreen: value })),

  currentContentMediumType: null,
  setCurrentContentMediumType: (value: ContentMediumType) => set({ currentContentMediumType: value }),

  isGeneratingYoutubeVideoTranscript: null,
  setIsGeneratingYoutubeVideoTranscript: (value: boolean) => set({ isGeneratingYoutubeVideoTranscript: value }),
}));

// ***** GENERATED CARDS ***** //
// ***** GENERATED CARDS ***** //

// TODO: finish doing this state so that deleting a card works
type GeneratedCardsStoreState = {
  // generatedCards: any[];
  // setGeneratedCards: (value: any[]) => void;
  // removeCardFromGeneratedCards: (cardId: string) => void;

  shouldShowEditOcclusionBoxesView: boolean;
  setShouldShowEditOcclusionBoxesView: (value: boolean) => void;

  showReferralMessageInCardsExperience: boolean;
  setShowReferralMessageInCardsExperience: (value: boolean) => void;
};

export const useGeneratedCardsStore = create<GeneratedCardsStoreState>((set) => ({
  shouldShowEditOcclusionBoxesView: false,
  setShouldShowEditOcclusionBoxesView: (value: boolean) => set({ shouldShowEditOcclusionBoxesView: value }),

  showReferralMessageInCardsExperience: false,
  setShowReferralMessageInCardsExperience: (value: boolean) => set({ showReferralMessageInCardsExperience: value }),
}));

// ***** LEGACY REDUX STATE ***** //
// ***** LEGACY REDUX STATE ***** //
// These are states that were in Redux and still need to be migrated
type LegacyReduxStoreState = {
  isAnswerTextareaFocused: boolean;
  setIsAnswerTextareaFocused: (value: boolean) => void;

  answerTextareaValue: string;
  setAnswerTextareaValue: (value: string) => void;

  isRegeneratingQuestions: boolean;
  setIsRegeneratingQuestions: (value: boolean) => void;

  didUserSelectIDontKnowOption: boolean;
  setDidUserSelectIDontKnowOption: (value: boolean) => void;

  showIsLocalPdfError: boolean;
  setShowIsLocalPdfError: (value: boolean) => void;

  shouldShowFreeResponseAnswerFeedbackView: boolean;
  setShouldShowFreeResponseAnswerFeedbackView: (value: boolean) => void;

  previousQuestionAndAnswerObjectToReview: CardData | null;
  setPreviousQuestionAndAnswerObjectToReview: (value: CardData | null) => void;

  didChooseToSaveAllGeneratedFlashcards: boolean;
  setDidChooseToSaveAllGeneratedFlashcards: (value: boolean) => void;

  lastDeckThatWasSavedTo: string;
  setLastDeckThatWasSavedTo: (value: string) => void;

  areFlashcardsBeingGenerated: boolean;
  setAreFlashcardsBeingGenerated: (value: boolean) => void;

  hasAnkiBeenSetUp: boolean;
  setHasAnkiBeenSetUp: (value: boolean) => void;

  deckToEdit: any | null;
  setDeckToEdit: (value: any | null) => void;

  isFlashcardBeingEdited: boolean;
  setIsFlashcardBeingEdited: (value: boolean) => void;

  youtubeTranscriptArrayText: Array<any> | null;
  setYoutubeTranscriptArrayText: (value: Array<any> | null) => void;

  isEnabledGenerateFlashcardButton: boolean;
  setIsEnabledGenerateFlashcardButton: (value: boolean) => void;

  pdfNumberOfPages: number | null;
  setPdfNumberOfPages: (value: number | null) => void;

  currentAnswerFeedbackId: any;
  setCurrentAnswerFeedbackId: (value: any) => void;

  isUploadingPDF: boolean;
  setIsUploadingPDF: (value: boolean) => void;

  pageTextSentencesArray: null | Array<string>;
  setPageTextSentencesArray: (value: Array<string> | null) => void;
};

export const legacyReduxStore = create<LegacyReduxStoreState>((set) => ({
  isAnswerTextareaFocused: false,
  setIsAnswerTextareaFocused: (value: boolean) => set({ isAnswerTextareaFocused: value }),

  answerTextareaValue: "",
  setAnswerTextareaValue: (value: string) => set({ answerTextareaValue: value }),

  isRegeneratingQuestions: false,
  setIsRegeneratingQuestions: (value: boolean) => set({ isRegeneratingQuestions: value }),

  didUserSelectIDontKnowOption: false,
  setDidUserSelectIDontKnowOption: (value: boolean) => set({ didUserSelectIDontKnowOption: value }),

  showIsLocalPdfError: false,
  setShowIsLocalPdfError: (value: boolean) => set({ showIsLocalPdfError: value }),

  shouldShowFreeResponseAnswerFeedbackView: false,
  setShouldShowFreeResponseAnswerFeedbackView: (value: boolean) =>
    set({ shouldShowFreeResponseAnswerFeedbackView: value }),

  previousQuestionAndAnswerObjectToReview: null,
  setPreviousQuestionAndAnswerObjectToReview: (value: CardData | null) =>
    set({ previousQuestionAndAnswerObjectToReview: value }),

  didChooseToSaveAllGeneratedFlashcards: false,
  setDidChooseToSaveAllGeneratedFlashcards: (value: boolean) => set({ didChooseToSaveAllGeneratedFlashcards: value }),

  lastDeckThatWasSavedTo: "Jungle",
  setLastDeckThatWasSavedTo: (value: string) => set({ lastDeckThatWasSavedTo: value }),

  areFlashcardsBeingGenerated: false,
  setAreFlashcardsBeingGenerated: (value: boolean) => set({ areFlashcardsBeingGenerated: value }),

  hasAnkiBeenSetUp: false,
  setHasAnkiBeenSetUp: (value: boolean) => set({ hasAnkiBeenSetUp: value }),

  deckToEdit: null,
  setDeckToEdit: (value: any | null) => set({ deckToEdit: value }),

  isFlashcardBeingEdited: false,
  setIsFlashcardBeingEdited: (value: boolean) => set({ isFlashcardBeingEdited: value }),

  youtubeTranscriptArrayText: null,
  setYoutubeTranscriptArrayText: (value: Array<any> | null) => set({ youtubeTranscriptArrayText: value }),

  isEnabledGenerateFlashcardButton: true,
  setIsEnabledGenerateFlashcardButton: (value: boolean) => set({ isEnabledGenerateFlashcardButton: value }),

  pdfNumberOfPages: null,
  setPdfNumberOfPages: (value: number | null) => set({ pdfNumberOfPages: value }),

  currentAnswerFeedbackId: null,
  setCurrentAnswerFeedbackId: (value: any) => set({ currentAnswerFeedbackId: value }),

  isUploadingPDF: false,
  setIsUploadingPDF: (value: boolean) => set({ isUploadingPDF: value }),

  pageTextSentencesArray: null,
  setPageTextSentencesArray: (value: Array<string> | null) => set({ pageTextSentencesArray: value }),
}));

// ***** CARDS STATE ***** //
// ***** CARDS STATE ***** //
export type UploadByteProgress = {
  /** The primary file whose transfer owns the upload screen. */
  fileName?: string;
  /** The upload attempt these counts belong to. */
  attemptId: string;
  loadedBytes: number;
  totalBytes: number;
  /** When the bytes started going to storage, and when the latest progress arrived (Date.now() ms). */
  startedAtMs: number;
  updatedAtMs: number;
};

export type CardStoreState = {
  isExplanationForUserAnswerVisible: boolean | null;
  setIsExplanationForUserAnswerVisible: (value: boolean | null) => void;

  shouldShowMCQOrTrueOrFalseQuestionAnswerSideNavigator: boolean;
  setShouldShowMCQOrTrueOrFalseQuestionAnswerSideNavigator: (value: boolean) => void;

  currentSpacedRepetitionReviewData: SpacedRepetitionReviewData | null;
  dailyReviewDeckActivityRevision: number;
  setCurrentSpacedRepetitionReviewData: (value: SpacedRepetitionReviewData | null) => void;

  // Class names cache for review cards
  dailyReviewClassNames: Set<string>;
  setDailyReviewClassNames: (value: Set<string>) => void;

  currentAnswerFromUserAndAiFeedbackObject: null | FreeResponseAnswerFromUserWithFeedback;
  setCurrentAnswerFromUserAndAiFeedbackObject: (value: FreeResponseAnswerFromUserWithFeedback) => void;

  currentAnswerFromUserAndAiFeedbackId: null | string;
  setCurrentAnswerFromUserAndAiFeedbackId: (value: string) => void;
  currentAnswerFromUserAndAiFeedbackPath: null | string;
  setCurrentAnswerFromUserAndAiFeedbackPath: (value: string) => void;
  showCardEditingPage: null | boolean;
  setShowCardEditingPage: (value: boolean) => void;

  indexOfCurrentMissedQuestionCard: null | number;
  setIndexOfCurrentMissedQuestionCard: (value: number) => void;

  shouldShowRetryMissedQuestionsReview: boolean;
  setShouldShowRetryMissedQuestionsReview: (value: boolean) => void;

  shouldShowSkippedCard: boolean;
  setShouldShowSkippedCard: (value: boolean) => void;

  shouldShowCheckpointWithPerformanceOverivewWithSkippedCard: boolean;
  setShouldShowCheckpointWithPerformanceOverivewWithSkippedCard: (value: boolean) => void;

  // It is used when user answer a question or change their knowledge rating.
  // For instance, if a user got a new mcq question correct, they will get 2 xp score xpScoreToAddForCurrentCard, but if they have answered it before incorrectly in prior session, they will get 1
  xpScoreToAddForCurrentCard: null | number;
  setXpScoreToAddForCurrentCard: (value: number) => void;

  // This is specific for user chaning the knowledge rating for free response questions on the card navigation component.
  // We need this to calculate the difference in xp score (xpScoreToAddForCurrentCard - previousProgressScoreForCard) so we can accurately add the correct xp score to the deck
  // For instance, if the user clicks don't know to okay, we need to add 2-1 = 1 to the deck score
  previousProgressScoreForCard: null | number;
  setPreviousProgressScoreForCard: (value: number) => void;

  // mini review mode is a very short 4 card review of different question types for first time users
  isUserInMiniReviewMode: boolean | null; // null by default, it will be false if the user had seen mini review and then is no longer in the mini review
  setIsUserInMiniReviewMode: (value: boolean) => void;

  topicUnderstandingDataPageDocumentIds: string[] | null;

  // his cardsData is used when you review recent mistakes for a checkpoint / finish view
  cardsDataForReviewingMistakes: CardData[] | null;
  setCardsDataForReviewingMistakes: (value: CardData[]) => void;

  showCheckpointWithPerformanceOverviewPage: null | boolean;
  setShowCheckpointWithPerformanceOverviewPage: (value: boolean) => void;

  showShareQuestionsInReviewCheckpointPage: null | boolean;
  setShowShareQuestionsInReviewCheckpointPage: (value: boolean) => void;

  hasShownShareQuestionsInReviewCheckpointPageInCurrentSession: boolean;
  setHasShownShareQuestionsInReviewCheckpointPageInCurrentSession: (value: boolean) => void;

  showLeaderboardLeagueResultsPage: null | boolean;
  setShowLeaderboardLeagueResultsPage: (value: boolean) => void;

  showDeckPerformanceOverviewPage: null | boolean;
  setShowDeckPerformanceOverviewPage: (value: boolean) => void;

  showReviewSessionCompletedWithJungleRiverScreen: null | boolean;
  setShowReviewSessionCompletedWithJungleRiverScreen: (value: boolean) => void;

  showEarnedBadgesReviewSessionCompletedScreen: null | boolean;
  setShowEarnedBadgesReviewSessionCompletedScreen: (value: boolean) => void;

  showGenerateMoreQuestionsUpsellPage: null | boolean;
  setShowGenerateMoreQuestionsUpsellPage: (value: boolean) => void;

  showCharacterSelectionAfterReviewCompletedScreen: boolean;
  setShowCharacterSelectionAfterReviewCompletedScreen: (value: boolean) => void;

  cardsForCheckpointWithPerformanceOverviewPage: Array<CardData> | null;
  setCardsForCheckpointWithPerformanceOverviewPage: (value: CardData[] | null) => void;

  videoOrAudioStartingMinute: number | null;
  setVideoOrAudioStartingMinute: (value: number | null) => void;

  videoOrAudioEndingMinute: number | null;
  setVideoOrAudioEndingMinute: (value: number | null) => void;

  cardsData: CardData[] | null;
  setCardsData: (cards: CardData[] | null, cardType?: CardType) => void;
  setUnorderedDataCards: (cards: CardData[]) => void;

  understandingRatingsForCardsData: CardData[] | null;
  setUnderstandingRatingsForCardsData: (cards: CardData[] | null) => void;

  allCardsForCurrentDeckAndSubdecks: CardData[] | null;
  setAllCardsForCurrentDeckAndSubdecks: (cards: CardData[] | null) => void;

  keyConcepts: KeyConcept[] | null;
  setKeyConcepts: (keyConcepts: KeyConcept[]) => void;

  currentKeyConcept: KeyConcept | null;
  setCurrentKeyConcept: (value: KeyConcept | null) => void;

  deleteCardFromCardsData: (cardId: string) => void;
  /** Takes several cards out of the review and the set at once; the review stays on its card, or moves on to the next. */
  removeCardsFromReview: (cardIdsToRemove: string[]) => void;
  updateCardFromCardsDataState: (updatedCard: CardData) => void;
  applyPersistedSharedAssessmentUnitCardEditToState: (
    assessmentUnitId: string,
    editedCardData: CardData,
    updatedSharedStimulus?: {
      blocks: AssessmentUnitStimulusBlock[];
      fallbackText: string;
    }
  ) => void;
  addCardToCardsDataState: (newCard: CardData, cardType?: CardType[]) => void;

  currentCardData: CardData | null;
  setCurrentCardData: (cards: CardData) => void;

  currentUserInteractionForPageGeneration: UserInteractionForPageGeneration | null;
  setCurrentUserInteractionForPageGeneration: (value: UserInteractionForPageGeneration) => void;

  indexOfCurrentCard: number;
  setIndexOfCurrentCard: (value: number) => void;

  loadingPercentage: number;
  setLoadingPercentage: (value: number) => void;
  /** Real bytes sent for the file being uploaded, shown under the upload loader ("2.1 of 8.4 MB"). */
  uploadByteProgress: UploadByteProgress | null;
  setUploadByteProgress: (value: UploadByteProgress | null) => void;

  cardsViewedWithoutSession: CardData[];
  setCardsViewedWithoutSession: (value: CardData[]) => void;
  addCardToCardsViewedWithoutSession: (value: CardData) => void;

  knowledgeUnderstandingRatingSelected: KnowledgeUnderstandingRating | null;
  setKnowledgeUnderstandingRatingSelected: (value: KnowledgeUnderstandingRating | null) => void;

  currentLearningJourneyModule: null | LearningJourneyModule;
  setCurrentLearningJourneyModule: (value: string) => void;

  understandingRatingsStateForNonAuthenticatedUser: UnderstandingRatingsForNonAuthenticatedUser | null;
  setUnderstandingRatingsStateForNonAuthenticatedUser: (
    value: UnderstandingRatingsForNonAuthenticatedUser | null
  ) => void;
  addUnderstandingRatingForCardForNonAuthenticatedUser: (value: CardData, knowledgeUnderstandingRating: number) => void;

  isChatWithDocumentTextAreaFocused: boolean;
  setIsChatWithDocumentTextAreaFocused: (value: boolean) => void;

  // New properties for selected cards
  selectedCards: CardData[];
  setSelectedCards: (cards: CardData[]) => void;
  addCardToSelectedCards: (card: CardData) => void;
  removeCardFromSelectedCards: (card: CardData) => void;

  secondToNavigateToInYoutubeViewer: number | null; // This is used to navigate to a specific second in the YouTube video viewer
  setSecondToNavigateToInYoutubeViewer: (page: number) => void;
  currentYoutubeTime: number; // Track current playback position in YouTube video
  setCurrentYoutubeTime: (time: number) => void;
  isYoutubePlaying: boolean; // Track whether YouTube video is playing
  setIsYoutubePlaying: (playing: boolean) => void;
};

// Cards taken out of the review during this page load by removeCardsFromReview ("remove write-in questions").
// Some components later write back a copy of a card list they captured before (a question's cleanup puts its
// copy of allCardsForCurrentDeckAndSubdecks back), which would bring the removed cards back: the setters leave
// them out.
const cardIdsRemovedFromReviewThisPageLoad = new Set<string>();
function withoutCardsRemovedFromReview(cards: CardData[]): CardData[] {
  if (cardIdsRemovedFromReviewThisPageLoad.size === 0) return cards;
  return cards.filter((card) => !cardIdsRemovedFromReviewThisPageLoad.has(card?.id));
}

export const cardStore = create<CardStoreState>((set, get) => ({
  // this is used to show the explanation for MCQ or personal feedback for free response questions after the user clicks on the explain my mistake or explain why it's right button
  isExplanationForUserAnswerVisible: false,
  setIsExplanationForUserAnswerVisible: (value: boolean | null) => set({ isExplanationForUserAnswerVisible: value }),

  shouldShowMCQOrTrueOrFalseQuestionAnswerSideNavigator: false,
  setShouldShowMCQOrTrueOrFalseQuestionAnswerSideNavigator: (value: boolean) =>
    set({ shouldShowMCQOrTrueOrFalseQuestionAnswerSideNavigator: value }),

  currentSpacedRepetitionReviewData: null,
  dailyReviewDeckActivityRevision: 0,
  setCurrentSpacedRepetitionReviewData: (value: SpacedRepetitionReviewData | null) =>
    set({ currentSpacedRepetitionReviewData: value }),

  // Class names cache for review cards
  dailyReviewClassNames: new Set<string>(),
  setDailyReviewClassNames: (value: Set<string>) => set({ dailyReviewClassNames: value }),

  // card answer feedback states
  currentAnswerFromUserAndAiFeedbackObject: null,
  setCurrentAnswerFromUserAndAiFeedbackObject: (value: FreeResponseAnswerFromUserWithFeedback) =>
    set({ currentAnswerFromUserAndAiFeedbackObject: value }),
  currentAnswerFromUserAndAiFeedbackId: null,
  setCurrentAnswerFromUserAndAiFeedbackId: (value: string) => set({ currentAnswerFromUserAndAiFeedbackId: value }),
  currentAnswerFromUserAndAiFeedbackPath: null,
  setCurrentAnswerFromUserAndAiFeedbackPath: (value: string) => set({ currentAnswerFromUserAndAiFeedbackPath: value }),
  showCardEditingPage: null,
  setShowCardEditingPage: (value: boolean) => set({ showCardEditingPage: value }),

  shouldShowRetryMissedQuestionsReview: null,
  setShouldShowRetryMissedQuestionsReview: (value: boolean) => set({ shouldShowRetryMissedQuestionsReview: value }),

  shouldShowCheckpointWithPerformanceOverivewWithSkippedCard: false,
  setShouldShowCheckpointWithPerformanceOverivewWithSkippedCard: (value: boolean) =>
    set({ shouldShowCheckpointWithPerformanceOverivewWithSkippedCard: value }),

  shouldShowSkippedCard: false,
  setShouldShowSkippedCard: (value: boolean) => set({ shouldShowSkippedCard: value }),

  xpScoreToAddForCurrentCard: null,
  setXpScoreToAddForCurrentCard: (value: number) => set({ xpScoreToAddForCurrentCard: value }),

  previousProgressScoreForCard: null,
  setPreviousProgressScoreForCard: (value: number) => set({ previousProgressScoreForCard: value }),

  indexOfCurrentMissedQuestionCard: null,
  setIndexOfCurrentMissedQuestionCard: (value: number) => set({ indexOfCurrentMissedQuestionCard: value }),

  showCheckpointWithPerformanceOverviewPage: null,
  setShowCheckpointWithPerformanceOverviewPage: (value: boolean) =>
    set({ showCheckpointWithPerformanceOverviewPage: value }),

  showShareQuestionsInReviewCheckpointPage: null,
  setShowShareQuestionsInReviewCheckpointPage: (value: boolean) =>
    set({ showShareQuestionsInReviewCheckpointPage: value }),

  hasShownShareQuestionsInReviewCheckpointPageInCurrentSession: false,
  setHasShownShareQuestionsInReviewCheckpointPageInCurrentSession: (value: boolean) =>
    set({ hasShownShareQuestionsInReviewCheckpointPageInCurrentSession: value }),

  showDeckPerformanceOverviewPage: null,
  setShowDeckPerformanceOverviewPage: (value: boolean) => set({ showDeckPerformanceOverviewPage: value }),

  isUserInMiniReviewMode: null,
  setIsUserInMiniReviewMode: (value: boolean) => set({ isUserInMiniReviewMode: value }),

  topicUnderstandingDataPageDocumentIds: null,

  cardsDataForReviewingMistakes: null,
  setCardsDataForReviewingMistakes: (value: CardData[]) => set({ cardsDataForReviewingMistakes: value }),

  showReviewSessionCompletedWithJungleRiverScreen: false,
  setShowReviewSessionCompletedWithJungleRiverScreen: (value: boolean) =>
    set({ showReviewSessionCompletedWithJungleRiverScreen: value }),

  showEarnedBadgesReviewSessionCompletedScreen: false,
  setShowEarnedBadgesReviewSessionCompletedScreen: (value: boolean) =>
    set({ showEarnedBadgesReviewSessionCompletedScreen: value }),

  showLeaderboardLeagueResultsPage: false,
  setShowLeaderboardLeagueResultsPage: (value: boolean) => set({ showLeaderboardLeagueResultsPage: value }),

  showGenerateMoreQuestionsUpsellPage: false,
  setShowGenerateMoreQuestionsUpsellPage: (value: boolean) => set({ showGenerateMoreQuestionsUpsellPage: value }),

  showCharacterSelectionAfterReviewCompletedScreen: false,
  setShowCharacterSelectionAfterReviewCompletedScreen: (value: boolean) =>
    set({ showCharacterSelectionAfterReviewCompletedScreen: value }),

  cardsForCheckpointWithPerformanceOverviewPage: null,
  setCardsForCheckpointWithPerformanceOverviewPage: (value: CardData[]) =>
    set({ cardsForCheckpointWithPerformanceOverviewPage: value }),

  videoOrAudioStartingMinute: 0,
  setVideoOrAudioStartingMinute: (value: number | null) => set({ videoOrAudioStartingMinute: value }),

  videoOrAudioEndingMinute: null,
  setVideoOrAudioEndingMinute: (value: number | null) => set({ videoOrAudioEndingMinute: value }),

  // cardsData contains the data for all the cards in a set of generated or saved cards that are being reviewed
  cardsData: null,
  setCardsData: (cardsDataToSet: CardData[] | null, cardType?: CardType) => {
    if (!cardsDataToSet) {
      set({ cardsData: null });
      return;
    }
    const cardsData = withoutCardsRemovedFromReview(cardsDataToSet);
    // filter out cards that are deleted and are not of the cardType specified
    if (!cardType) {
      if (get().isUserInMiniReviewMode) {
        set({ cardsData: orderCardsForReviewByAssessmentUnit(cardsData) });
        return;
      }
      const cardsOrderedForLegacyReviewBehavior =
        orderCardsToHaveTheFirstFourBeMultipleChoiceQuestionsAndGroupCaseQuestions(cardsData, get().indexOfCurrentCard);
      set({
        cardsData: orderCardsForReviewByAssessmentUnit(cardsOrderedForLegacyReviewBehavior),
      });
      return;
    }
    let filteredCards = cardsData.filter((card) => !card.is_deleted && (!cardType || card?.card_type === cardType));
    if (cardType === CardType.CaseScenarioFreeResponseQuestion) {
      filteredCards = orderCardsByCaseScenario(filteredCards);
    }

    set({ cardsData: orderCardsForReviewByAssessmentUnit(filteredCards) });
  },
  setUnorderedDataCards: (cardsData: CardData[]) => {
    set({ cardsData: cardsData });
  },

  understandingRatingsForCardsData: null,
  setUnderstandingRatingsForCardsData: (cardsData: CardData[] | null) =>
    set({ understandingRatingsForCardsData: cardsData }),

  allCardsForCurrentDeckAndSubdecks: null,
  setAllCardsForCurrentDeckAndSubdecks: (cardsData: CardData[] | null) => {
    if (!cardsData) {
      set({ allCardsForCurrentDeckAndSubdecks: null });
      return;
    }
    // filter out cards that are deleted
    const filteredCards = withoutCardsRemovedFromReview(cardsData).filter((card) => !card.is_deleted);

    set({ allCardsForCurrentDeckAndSubdecks: filteredCards });
  },

  keyConcepts: null,
  setKeyConcepts: (value: KeyConcept[]) => {
    set((state) => {
      if (!state.currentKeyConcept && value?.length > 0) {
        return { keyConcepts: value, currentKeyConcept: value[0] };
      } else {
        let new_keyConcept = value?.find((keyConcept) => keyConcept.id === state.currentKeyConcept?.id);
        if (new_keyConcept) {
          set({ currentKeyConcept: new_keyConcept });
        }
        return { keyConcepts: value };
      }
    });
  },

  currentKeyConcept: null,
  setCurrentKeyConcept: (keyConceptData: KeyConcept | null) => {
    set((state) => {
      if (!keyConceptData) {
        return { currentKeyConcept: null };
      }

      return { currentKeyConcept: keyConceptData };
    });
  },

  updateCardFromCardsDataState: (updatedCard: CardData) => {
    set((state) => {
      // OpenEndedQuestion was the default card type for questions before learning journeys
      // so, if the card is an open ended question, change it to a pre-learning journey question to avoid confusion
      if (updatedCard?.card_type === CardType.OpenEndedQuestion) {
        updatedCard.card_type = CardType.RegularFreeResponseQuestion;
      }

      // Ensure version fields are present on the updated card
      const userId = localStorage.getItem("wuId");
      const minimalUser = userId ? ({ id: userId } as any) : undefined;
      const updatedCardWithVersion = (() => {
        const hasQuestionVersion =
          updatedCard.questionVersionBeingUsed !== undefined &&
          updatedCard.questionVersionBeingUsed !== null &&
          updatedCard.questionVersionBeingUsed !== "";
        const hasIndexVersion = updatedCard.rephrasedQuestionVersionIndexBeingUsed !== undefined;
        if (hasQuestionVersion && hasIndexVersion) return updatedCard;
        const versionData = getQuestionVersionToDisplay(updatedCard, minimalUser);
        return updateCardWithVersionBeingUsed(updatedCard, versionData);
      })();

      let cardsData = state.cardsData?.map((currentCard) => {
        if (currentCard.id === updatedCardWithVersion.id) {
          if (
            currentCard?.understanding_ratings_for_card_by_user &&
            !updatedCardWithVersion?.understanding_ratings_for_card_by_user
          ) {
            // the purpose of this if to make sure that the understanding_ratings_for_card_by_user is not overwritten
            updatedCardWithVersion.understanding_ratings_for_card_by_user =
              currentCard?.understanding_ratings_for_card_by_user;
          }
          return updatedCardWithVersion;
        } else {
          return currentCard;
        }
      });
      // deletion is handled with the is_deleted field because a card can exist in many different decks
      cardsData = cardsData?.filter((currentCard) => {
        return !currentCard.is_deleted;
      });

      // update cardData on cardsDataForReviewingMistakes
      if (state.cardsDataForReviewingMistakes) {
        let cardsDataForReviewingMistakes = state.cardsDataForReviewingMistakes?.map((currentCard) => {
          if (currentCard.id === updatedCardWithVersion.id) {
            return updatedCardWithVersion;
          } else {
            return currentCard;
          }
        });
        set({ cardsDataForReviewingMistakes: cardsDataForReviewingMistakes });
      }

      // Update currentCardData if it matches the updated card, regardless of whether we're in retry mode or not
      if (state.currentCardData?.id === updatedCardWithVersion.id) {
        return {
          cardsData: cardsData,
          currentCardData: updatedCardWithVersion?.is_deleted ? null : updatedCardWithVersion,
        };
      }

      return { cardsData: cardsData };
    });
  },

  applyPersistedSharedAssessmentUnitCardEditToState: (assessmentUnitId, editedCardData, updatedSharedStimulus) => {
    set((state) => {
      const applyPersistedEditToCard = (cardData: CardData): CardData => {
        if (editedCardData.id && cardData.id === editedCardData.id) {
          return editedCardData;
        }
        if (updatedSharedStimulus === undefined || cardData.assessment_unit_id !== assessmentUnitId) {
          return cardData;
        }
        const isMultipleChoiceSiblingCard =
          cardData.card_type === CardType.RegularMultipleChoiceQuestion ||
          cardData.card_type === CardType.CaseScenarioMultipleChoiceQuestion ||
          cardData.card_type === CardType.USMLEStep1MCQ;
        return {
          ...cardData,
          case_scenario_details: updatedSharedStimulus.fallbackText,
          assessment_unit_stimulus_schema_version: 1,
          assessment_unit_stimulus: updatedSharedStimulus.blocks,
          assessment_unit_stimulus_override_markdown: undefined,
          // The passage every sibling is graded against just changed, so a
          // sibling's in-session answer no longer describes a valid attempt.
          // Reset it the same way EditCardPage resets the edited card itself.
          // Earned XP and rating history are records of past attempts and are
          // intentionally left alone.
          selectedAnswerText: isMultipleChoiceSiblingCard ? null : undefined,
        };
      };

      return {
        cardsData: state.cardsData?.map(applyPersistedEditToCard) ?? state.cardsData,
        cardsDataForReviewingMistakes:
          state.cardsDataForReviewingMistakes?.map(applyPersistedEditToCard) ?? state.cardsDataForReviewingMistakes,
        allCardsForCurrentDeckAndSubdecks:
          state.allCardsForCurrentDeckAndSubdecks?.map(applyPersistedEditToCard) ??
          state.allCardsForCurrentDeckAndSubdecks,
        currentCardData: state.currentCardData
          ? applyPersistedEditToCard(state.currentCardData)
          : state.currentCardData,
      };
    });
  },

  deleteCardFromCardsData: (cardId: string) => {
    set((state) => {
      const updatedCardsData = state.cardsData?.filter((card) => card.id !== cardId);
      let updatedCurrentCardData = state.currentCardData?.id === cardId ? null : state.currentCardData;

      // remove card from the all cards data
      const allCardsForCurrentDeckAndSubdecks = state.allCardsForCurrentDeckAndSubdecks;
      if (allCardsForCurrentDeckAndSubdecks) {
        const updatedAllCardsCardsData = allCardsForCurrentDeckAndSubdecks.filter((card) => card.id !== cardId);
        set({ allCardsForCurrentDeckAndSubdecks: updatedAllCardsCardsData });
      }
      // remove card from the cardsDataForReviewingMistakes when the user is reviewing missed questions
      if (state.shouldShowRetryMissedQuestionsReview) {
        const missedCardsExcludingCurrentCard = state.cardsDataForReviewingMistakes.filter(
          (card) => card.id !== cardId
        );
        state.setCardsDataForReviewingMistakes(missedCardsExcludingCurrentCard);

        if (!updatedCurrentCardData && missedCardsExcludingCurrentCard.length > 0) {
          const currentMissedCardIndex = state.indexOfCurrentMissedQuestionCard;
          const safeMissedCardIndex = Math.min(currentMissedCardIndex, missedCardsExcludingCurrentCard.length - 1);
          updatedCurrentCardData = missedCardsExcludingCurrentCard[safeMissedCardIndex];
          state.setIndexOfCurrentMissedQuestionCard(safeMissedCardIndex);
        }
      }

      return { cardsData: updatedCardsData, currentCardData: updatedCurrentCardData };
    });
  },

  removeCardsFromReview: (cardIdsToRemove: string[]) => {
    const removedCardIds = new Set(cardIdsToRemove);
    if (removedCardIds.size === 0) return;
    removedCardIds.forEach((removedCardId) => cardIdsRemovedFromReviewThisPageLoad.add(removedCardId));
    // One transition for every list, so no render sees the review's order and its position disagree
    set((state) => {
      const cardsDataBeforeRemoval = state.cardsData ?? [];
      const remainingCardsData = cardsDataBeforeRemoval.filter((card) => !removedCardIds.has(card?.id));
      const indexOfCurrentCard = getReviewIndexAfterRemovingCards({
        cardsBeforeRemoval: cardsDataBeforeRemoval,
        selectedIndexBeforeRemoval: state.indexOfCurrentCard,
        removedCardIds,
      });

      const missedCardsBeforeRemoval = state.cardsDataForReviewingMistakes ?? [];
      const remainingMissedCards = missedCardsBeforeRemoval.filter((card) => !removedCardIds.has(card?.id));
      const indexOfCurrentMissedQuestionCard =
        state.indexOfCurrentMissedQuestionCard === null
          ? null
          : getReviewIndexAfterRemovingCards({
              cardsBeforeRemoval: missedCardsBeforeRemoval,
              selectedIndexBeforeRemoval: state.indexOfCurrentMissedQuestionCard,
              removedCardIds,
            });

      const reviewCardsAfterRemoval = state.shouldShowRetryMissedQuestionsReview
        ? remainingMissedCards
        : remainingCardsData;
      const indexInReviewAfterRemoval = state.shouldShowRetryMissedQuestionsReview
        ? indexOfCurrentMissedQuestionCard
        : indexOfCurrentCard;
      const currentCardData = removedCardIds.has(state.currentCardData?.id)
        ? (reviewCardsAfterRemoval[indexInReviewAfterRemoval] ?? null)
        : state.currentCardData;

      return {
        cardsData: state.cardsData ? remainingCardsData : state.cardsData,
        indexOfCurrentCard,
        cardsDataForReviewingMistakes: state.cardsDataForReviewingMistakes
          ? remainingMissedCards
          : state.cardsDataForReviewingMistakes,
        indexOfCurrentMissedQuestionCard,
        allCardsForCurrentDeckAndSubdecks: state.allCardsForCurrentDeckAndSubdecks?.filter(
          (card) => !removedCardIds.has(card?.id)
        ),
        currentCardData,
      };
    });
  },

  // if cardType(s) are passed in as a param, we'll filter cardsData to only include these cardType(s)
  addCardToCardsDataState: (newCard: CardData, cardTypes?: CardType[]) => {
    set((state) => {
      if (newCard?.is_deleted || newCard?.should_hide_card || !newCard?.question || !newCard?.answer) return {}; // we used to delete cards by setting is_deleted to true
      if (cardIdsRemovedFromReviewThisPageLoad.has(newCard?.id)) return {};

      // OpenEndedQuestion was the default card type for questions before learning journeys
      // so, if the card is an open ended question, change it to a pre-learning journey question to avoid confusion
      if (newCard?.card_type === CardType.OpenEndedQuestion) {
        newCard.card_type = CardType.RegularFreeResponseQuestion;
      }

      let cardsData = state.cardsData ? state.cardsData : [];

      const isCardDuplicate = cardsData.some((currentCardIteration) => currentCardIteration?.id === newCard?.id);
      // if the card is a duplicate, we'll ignore it
      // important to note that we could update the card that is a duplicate but it's not necessary for now
      if (isCardDuplicate) {
        return {};
      }

      // if the new card is different from current card, we'll update the state
      if (state.currentCardData?.id === newCard?.id) {
        // Create a copy of currentCardData without current_user_answer_input for comparison
        const { current_user_answer_input, ...currentCardDataWithoutInput } = state.currentCardData;
        const newCardWithoutInput = { ...newCard };
        delete newCardWithoutInput.current_user_answer_input; // Remove the field for comparison

        // Check if the two objects are not equal
        if (!isEqual(currentCardDataWithoutInput, newCardWithoutInput)) {
          // let's update everything except the current_user_answer_input
          const updatedCard = {
            ...newCard,
            current_user_answer_input: state.currentCardData.current_user_answer_input,
          };
          set({ currentCardData: updatedCard });
        }
      }

      // cardTypes will only have a value if the user selected to study a specific card type
      if (!cardTypes) {
        function sortCardsAfterCurrentIndex(cardsData: CardData[]) {
          // Sort only the cards after the current index
          const currentIndex = get().indexOfCurrentCard;
          const cardsBeforeCurrent = cardsData.slice(0, currentIndex + 1);
          const cardsAfterCurrent = cardsData.slice(currentIndex + 1);

          const sortedCardsAfterCurrent = cardsAfterCurrent.sort((a, b) => {
            const aIndex = a?.card_index ?? 999;
            const bIndex = b?.card_index ?? 999;
            if (aIndex === bIndex) {
              // If card index is the same, sort by question
              return (a?.question ?? "").localeCompare(b?.question ?? "");
            }
            return aIndex - bIndex;
          });

          return [...cardsBeforeCurrent, ...sortedCardsAfterCurrent];
        }

        cardsData = [...cardsData, newCard];
        cardsData = sortCardsAfterCurrentIndex(cardsData);
        cardsData = orderCardsToHaveTheFirstFourBeMultipleChoiceQuestionsAndGroupCaseQuestions(
          cardsData,
          get().indexOfCurrentCard
        );
      }

      // group case scenario questions so that they are right next to each other
      else if (
        (cardTypes.includes(CardType.CaseScenarioFreeResponseQuestion) ||
          cardTypes.includes(CardType.CaseScenarioMultipleChoiceQuestion)) &&
        newCard.case_scenario_details
      ) {
        cardsData = [...cardsData, newCard];
        cardsData = orderCardsByCaseScenario(cardsData);
      } else if (cardTypes.includes(newCard.card_type)) {
        cardsData = [...cardsData, newCard];
      }
      return { cardsData: cardsData };
    });
  },

  currentCardData: null,
  setCurrentCardData: (cardData: CardData) =>
    set(() => {
      if (!cardData) return { currentCardData: null };
      // OpenEndedQuestion was the default card type for questions before learning journeys
      // so, if the card is an open ended question, change it to a pre-learning journey question to avoid confusion
      if (cardData?.card_type === CardType.OpenEndedQuestion) {
        cardData.card_type = CardType.RegularFreeResponseQuestion;
      }
      // Attach version fields if missing
      const hasQuestionVersion =
        cardData?.questionVersionBeingUsed !== undefined &&
        cardData?.questionVersionBeingUsed !== null &&
        cardData?.questionVersionBeingUsed !== "";
      const hasIndexVersion = cardData?.rephrasedQuestionVersionIndexBeingUsed !== undefined;
      let cardWithVersion = cardData;
      if (!(hasQuestionVersion && hasIndexVersion)) {
        const userId = localStorage.getItem("wuId");
        const minimalUser = userId ? ({ id: userId } as any) : undefined;
        const versionData = getQuestionVersionToDisplay(cardData, minimalUser);
        cardWithVersion = updateCardWithVersionBeingUsed(cardData, versionData);
      }

      return { currentCardData: cardWithVersion };
    }),

  currentUserInteractionForPageGeneration: null,
  setCurrentUserInteractionForPageGeneration: (value: UserInteractionForPageGeneration) =>
    set({ currentUserInteractionForPageGeneration: value }),

  indexOfCurrentCard: 0,
  setIndexOfCurrentCard: (index: number) =>
    set((state) => {
      const cardsLength = state.cardsData?.length || 0;
      const safeIndex = index >= cardsLength ? Math.max(0, cardsLength - 1) : index;
      return { indexOfCurrentCard: safeIndex };
    }),

  // this is for making sure users that are not logged in can only view 3 cards
  cardsViewedWithoutSession: [],
  setCardsViewedWithoutSession: (cardData: CardData[]) => set({ cardsViewedWithoutSession: cardData }),
  addCardToCardsViewedWithoutSession: (cardData: CardData) => {
    set((state) => {
      // OpenEndedQuestion was the default card type for questions before learning journeys
      // so, if the card is an open ended question, change it to a pre-learning journey question to avoid confusion
      if (cardData?.card_type === CardType.OpenEndedQuestion) {
        cardData.card_type = CardType.RegularFreeResponseQuestion;
      }

      const index = state.cardsViewedWithoutSession.findIndex((card) => card?.id === cardData?.id);
      if (index !== -1) {
        state.cardsViewedWithoutSession[index] = cardData;
      } else {
        state.cardsViewedWithoutSession.push(cardData);
      }
      localStorage.setItem("cardsViewedWithoutSession", JSON.stringify(state.cardsViewedWithoutSession));

      return { cardsViewedWithoutSession: state.cardsViewedWithoutSession };
    });
  },

  knowledgeUnderstandingRatingSelected: null,
  setKnowledgeUnderstandingRatingSelected: (value: KnowledgeUnderstandingRating | null) =>
    set({ knowledgeUnderstandingRatingSelected: value }),

  loadingPercentage: null,
  setLoadingPercentage: (value: number) => set({ loadingPercentage: value }),
  uploadByteProgress: null,
  setUploadByteProgress: (value: UploadByteProgress | null) => set({ uploadByteProgress: value }),

  currentLearningJourneyModule: null,
  setCurrentLearningJourneyModule: (value: LearningJourneyModule) => set({ currentLearningJourneyModule: value }),

  understandingRatingsStateForNonAuthenticatedUser: null,
  setUnderstandingRatingsStateForNonAuthenticatedUser: (value: UnderstandingRatingsForNonAuthenticatedUser | null) =>
    set({ understandingRatingsStateForNonAuthenticatedUser: value }),
  addUnderstandingRatingForCardForNonAuthenticatedUser: (value: CardData, knowledgeUnderstandingRating: number) => {
    set((state) => {
      let updatedCardUnderstandingRatings = state.understandingRatingsStateForNonAuthenticatedUser
        ? state.understandingRatingsStateForNonAuthenticatedUser
        : localStorage.getItem("understandingRatingsStateForNonAuthenticatedUser")
          ? JSON.parse(localStorage.getItem("understandingRatingsStateForNonAuthenticatedUser"))
          : {};
      if (knowledgeUnderstandingRating >= 3) {
        let iKnowItCards = updatedCardUnderstandingRatings.iKnowItCards
          ? updatedCardUnderstandingRatings.iKnowItCards
          : {};
        iKnowItCards[value.id] = value;
        updatedCardUnderstandingRatings.iKnowItCards = iKnowItCards;
      } else if (knowledgeUnderstandingRating >= 2) {
        let okayCards = updatedCardUnderstandingRatings.okayCards ? updatedCardUnderstandingRatings.okayCards : {};
        okayCards[value.id] = value;
        updatedCardUnderstandingRatings.okayCards = okayCards;
      } else if (knowledgeUnderstandingRating >= 1) {
        let notSureCards = updatedCardUnderstandingRatings.notSureCards
          ? updatedCardUnderstandingRatings.notSureCards
          : {};
        notSureCards[value.id] = value;
        updatedCardUnderstandingRatings.notSureCards = notSureCards;
      }
      localStorage.setItem(
        "understandingRatingsStateForNonAuthenticatedUser",
        JSON.stringify(updatedCardUnderstandingRatings)
      );
      return { understandingRatingsStateForNonAuthenticatedUser: updatedCardUnderstandingRatings };
    });
  },
  isChatWithDocumentTextAreaFocused: false,
  setIsChatWithDocumentTextAreaFocused: (value: boolean) => set({ isChatWithDocumentTextAreaFocused: value }),

  // New implementations for selected cards
  selectedCards: [],
  setSelectedCards: (cards) => set({ selectedCards: cards }),
  addCardToSelectedCards: (card) => {
    set((state) => ({
      selectedCards: [...state.selectedCards, card],
    }));
  },
  removeCardFromSelectedCards: (card) => {
    set((state) => ({
      selectedCards: state.selectedCards.filter((c) => c.id !== card.id),
    }));
  },

  secondToNavigateToInYoutubeViewer: undefined,
  setSecondToNavigateToInYoutubeViewer: (page: number) => set({ secondToNavigateToInYoutubeViewer: page }),
  currentYoutubeTime: 0,
  setCurrentYoutubeTime: (time: number) => set({ currentYoutubeTime: time }),
  isYoutubePlaying: false,
  setIsYoutubePlaying: (playing: boolean) => set({ isYoutubePlaying: playing }),
}));

// ***** USER STATE ***** //
// ***** USER STATE ***** //
type UserStoreState = {
  currentUser: UserData | null | undefined;
  setCurrentUser: (user: UserData | null | undefined) => void;
  updateCurrentUser: (userUpdates: Partial<UserData>) => void;

  userIdFromLocalStorage: null | string;
  setUserIdFromLocalStorage: (userIdFromLocalStorage: string | null) => void;

  userDecks: DeckObject[];
  setUserDecks: (userDecks: DeckObject[]) => void;

  currentNumberOfTrees: number;
  setCurrentNumberOfTrees: (value: number) => void;

  addNewDeckToUserDecks: (newDeck: DeckObject) => void;
  deleteDeckFromUserDecks: (deckId: string) => void;
  updateDeckInUserDecks: (updatedDeck: DeckObject) => void;

  shouldShowCancellationReasonAndFeedbackScreen: boolean;
  setShouldShowCancellationReasonAndFeedbackScreen: (value: boolean) => void;
};

export const userStore = create<UserStoreState>((set) => ({
  currentUser: undefined,
  setCurrentUser: (user: UserData | null | undefined) =>
    set((state) => ({
      currentUser: user,
      // Authentication can remount the class page. Invalidate the shared cache
      // at the account boundary so a new mount never adopts the old account's rows.
      ...(state.currentUser?.id !== user?.id ? { userDecks: [] } : {}),
    })),
  updateCurrentUser: (userUpdates: Partial<UserData>) =>
    set((state) => {
      if (!state.currentUser) return state;
      return { currentUser: { ...state.currentUser, ...userUpdates } };
    }),

  userIdFromLocalStorage: localStorage.getItem("wuId"),
  setUserIdFromLocalStorage: (value: string | null) => set({ userIdFromLocalStorage: value }),

  userDecks: [],
  setUserDecks: (value: DeckObject[]) => set({ userDecks: value }),

  currentNumberOfTrees: 0,
  setCurrentNumberOfTrees: (value: number) => set({ currentNumberOfTrees: value }),

  addNewDeckToUserDecks: (newDeck: DeckObject) =>
    set((state) => ({
      userDecks: [...state.userDecks, newDeck],
    })),
  deleteDeckFromUserDecks: (deckId) =>
    set((state) => ({
      userDecks: state.userDecks.filter((deck) => deck.id !== deckId),
    })),
  updateDeckInUserDecks: (updatedDeck: DeckObject) =>
    set((state) => ({
      userDecks: state.userDecks.map((deck) => (deck.id === updatedDeck.id ? updatedDeck : deck)),
    })),

  shouldShowCancellationReasonAndFeedbackScreen: false,
  setShouldShowCancellationReasonAndFeedbackScreen: (value: boolean) =>
    set({ shouldShowCancellationReasonAndFeedbackScreen: value }),
}));

// ***** NAVIGATION WITH STATES ***** //
// ***** NAVIGATION WITH STATES ***** //
/** The exam the notifications opt-in leads with: "get a heads-up before {examName}". */
export type UpcomingExamForNotificationCopy = {
  examName: string;
  daysUntilExam: number;
};

/** Why the desktop notifications opt-in screen is showing, and the exam its copy leads with. */
export type DesktopNotificationsOptInScreenContext = {
  whyWereShowingThisScreen: string;
  // undefined while the learner's next exam is still being looked up (the screen waits), null when there is none
  upcomingExam: UpcomingExamForNotificationCopy | null | undefined;
};

type NavigationWithStatesStore = {
  showAddYourExamAfterMiniReviewPage: boolean;
  setShowAddYourExamAfterMiniReviewPage: (value: boolean) => void;

  showDesktopNotificationsOptInScreen: boolean;
  setShowDesktopNotificationsOptInScreen: (value: boolean) => void;

  desktopNotificationsOptInScreenContext: DesktopNotificationsOptInScreenContext | null;
  setDesktopNotificationsOptInScreenContext: (value: DesktopNotificationsOptInScreenContext | null) => void;

  showNotificationPermissionInstructionsModal: boolean;
  setShowNotificationPermissionInstructionsModal: (value: boolean) => void;

  // Microphone permissions modal for live recording
  showMicrophonePermissionInstructionsModal: boolean;
  setShowMicrophonePermissionInstructionsModal: (value: boolean) => void;

  didShowNotificationOptInScreenForCurrentNumberOfReviewSessionsStarted: boolean;
  setDidShowNotificationOptInScreenForCurrentNumberOfReviewSessionsStarted: (value: boolean) => void;
};

export const navigationWithStatesStore = create<NavigationWithStatesStore>((set) => ({
  showAddYourExamAfterMiniReviewPage: false,
  setShowAddYourExamAfterMiniReviewPage: (value: boolean) => set({ showAddYourExamAfterMiniReviewPage: value }),

  showDesktopNotificationsOptInScreen: false,
  setShowDesktopNotificationsOptInScreen: (value: boolean) => {
    // Only set to true if user is not in incognito mode
    if (value === true) {
      // Check if browser is in incognito/private mode
      const isIncognito = !window.indexedDB || !window.localStorage || !window.sessionStorage;

      if (!isIncognito) {
        set({ showDesktopNotificationsOptInScreen: true });
      } else {
        set({ showDesktopNotificationsOptInScreen: false });
      }
    } else {
      // Always allow setting to false
      set({ showDesktopNotificationsOptInScreen: false });
    }
  },

  desktopNotificationsOptInScreenContext: null,
  setDesktopNotificationsOptInScreenContext: (value: DesktopNotificationsOptInScreenContext | null) =>
    set({ desktopNotificationsOptInScreenContext: value }),

  showNotificationPermissionInstructionsModal: false,
  setShowNotificationPermissionInstructionsModal: (value: boolean) =>
    set({ showNotificationPermissionInstructionsModal: value }),

  // Microphone permissions modal for live recording
  showMicrophonePermissionInstructionsModal: false,
  setShowMicrophonePermissionInstructionsModal: (value: boolean) =>
    set({ showMicrophonePermissionInstructionsModal: value }),

  didShowNotificationOptInScreenForCurrentNumberOfReviewSessionsStarted: false,
  setDidShowNotificationOptInScreenForCurrentNumberOfReviewSessionsStarted: (value: boolean) =>
    set({ didShowNotificationOptInScreenForCurrentNumberOfReviewSessionsStarted: value }),
}));

// ***** TRACKING STATE ***** //
// ***** TRACKING STATE ***** //
type TrackingStoreState = {
  userReferrerId: null | string;
  setUserReferrerId: (value: string | null) => void;

  tiktokCampaignId: null | string;
  setTiktokCampaignId: (value: string) => void;
  tiktokClickId: null | string; // this value comes from a query parameter passed by tiktok named ttclid
  setTiktokClickId: (value: string) => void;

  didUserComeFromMetaAd: boolean;
  setDidUserComeFromMetaAd: (value: boolean) => void;

  googleCampaignId: null | string;
  setGoogleCampaignId: (value: string) => void;

  redditCampaignId: null | string;
  setRedditCampaignId: (value: string) => void;

  influencerCampaignId: null | string;
  setInfluencerCampaignId: (value: string) => void;

  newsletterCampaignId: null | string;
  setNewsletterCampaignId: (value: string) => void;

  idOfAmbassadorThatReferredUser: null | string;
  setIdOfAmbassadorThatReferredUser: (value: string) => void;
};

export const trackingStore = create<TrackingStoreState>((set) => ({
  userReferrerId: null,
  setUserReferrerId: (value: string | null) => set({ userReferrerId: value }),

  tiktokCampaignId: null,
  setTiktokCampaignId: (value: string) => set({ tiktokCampaignId: value }),
  tiktokClickId: null,
  setTiktokClickId: (value: string) => set({ tiktokClickId: value }),

  didUserComeFromMetaAd: false,
  setDidUserComeFromMetaAd: (value: boolean) => set({ didUserComeFromMetaAd: value }),

  googleCampaignId: null,
  setGoogleCampaignId: (value: string) => set({ googleCampaignId: value }),

  redditCampaignId: null,
  setRedditCampaignId: (value: string) => set({ redditCampaignId: value }),

  influencerCampaignId: null,
  setInfluencerCampaignId: (value: string) => set({ influencerCampaignId: value }),

  newsletterCampaignId: null,
  setNewsletterCampaignId: (value: string) => set({ newsletterCampaignId: value }),

  idOfAmbassadorThatReferredUser: null,
  setIdOfAmbassadorThatReferredUser: (value: string) => set({ idOfAmbassadorThatReferredUser: value }),
}));

// ***** ASK QUESTIONS CHAT STATE ***** //
// ***** ASK QUESTIONS CHAT STATE ***** //
type AskQuestionsChatStoreState = {
  chatMessages: Array<ChatMessage> | null;
  setChatMessages: (chatMessages: Array<ChatMessage>) => void;
  addMessageToChatMessages: (newChatMessage: ChatMessage) => void;
  removeLastTwoMessagesFromChatMessages: () => void;

  initialUserMessageForChat: string; // This is the message that will be sent to the chat when the user opens it but hasn't sent any messages yet
  setInitialUserMessageForChat: (value: string) => void;

  shouldShowTabsInChatSidePanel: boolean; // This is used to display the chat side panel with tab buttons for switching between chat view and source view
  setShouldShowTabsInChatSidePanel: (value: boolean) => void;

  chatSidePanelParameters: ChatSidePanelProps | null; // This is used to pass parameters when the chat side panel is opened
  setChatSidePanelParameters: (value: ChatSidePanelProps) => void;

  // Legacy - removed in favor of usePanelNavigationStore activeTabId
  isAskQuestionsChatModalOpened: boolean;
  // DEPRECATED: Use SidePanelOrchestrator instead
  // setIsAskQuestionsChatModalOpened: (
  //   isAskQuestionsChatModalOpened: boolean,
  //   chatSidePanelProps?: ChatSidePanelProps
  // ) => void;

  isViewingChatForASpecificCard: boolean;

  // Chat scroll position tracking
  chatScrollPosition: number;
  setChatScrollPosition: (position: number) => void;
  shouldRestoreScrollPosition: boolean;
  setShouldRestoreScrollPosition: (shouldRestore: boolean) => void;

  // Indicates where the chat is being used (deck review flow vs live recording)
  chatLocation: SidePanelLocation;
  setChatLocation: (location: SidePanelLocation) => void;

  // Reference to active recording when in live-recording chat location
  activeRecordingId: string | null;
  setActiveRecordingId: (id: string | null) => void;

  // Clear messages when switching chat locations
  clearChatForLocationSwitch: () => void;
};

export const askQuestionsChatStore = create<AskQuestionsChatStoreState>((set) => ({
  chatMessages: null,
  setChatMessages: (chatMessages: Array<ChatMessage>) => set({ chatMessages: chatMessages }),
  addMessageToChatMessages: (newChatMessage: ChatMessage) =>
    set((state) => {
      if (!state.chatMessages) {
        return { chatMessages: [newChatMessage] };
      } else {
        return { chatMessages: [...state.chatMessages, newChatMessage] };
      }
    }),
  removeLastTwoMessagesFromChatMessages: () =>
    set((state) => {
      // `>= 2` (not `> 2`) — when there are EXACTLY two messages (the failed
      // user+assistant pair from a first-turn retry), we still want to drop
      // them. The previous `> 2` no-op'd in that case, which left the failed
      // user message in place; `handleRetryLastMessage` would then append a
      // second copy of the user's text, producing a visible duplicate.
      if (state.chatMessages && state.chatMessages.length >= 2) {
        return { chatMessages: state.chatMessages.slice(0, -2) };
      }
      return { chatMessages: state.chatMessages };
    }),

  initialUserMessageForChat: null,
  setInitialUserMessageForChat: (value: string) => set({ initialUserMessageForChat: value }),

  shouldShowTabsInChatSidePanel: false,
  setShouldShowTabsInChatSidePanel: (value: boolean) => set({ shouldShowTabsInChatSidePanel: value }),

  chatSidePanelParameters: null,
  setChatSidePanelParameters: (value: ChatSidePanelProps) => set({ chatSidePanelParameters: value }),

  isViewingChatForASpecificCard: false,

  // Legacy properties removed - use usePanelNavigationStore instead

  isAskQuestionsChatModalOpened: false,
  // DEPRECATED: This function has been replaced by SidePanelOrchestrator
  // Keep for reference only - DO NOT USE
  // setIsAskQuestionsChatModalOpened: (
  //   isAskQuestionsChatModalOpened: boolean,
  //   chatSidePanelProps: ChatSidePanelProps | null = null
  // ) => {
  //   // Implementation removed - use SidePanelOrchestrator instead
  // },

  // Chat scroll position tracking
  chatScrollPosition: 0,
  setChatScrollPosition: (position: number) => set({ chatScrollPosition: position }),
  shouldRestoreScrollPosition: false,
  setShouldRestoreScrollPosition: (shouldRestore: boolean) => set({ shouldRestoreScrollPosition: shouldRestore }),

  // Context awareness for multi-tab refactoring
  chatLocation: SidePanelLocation.DECK_REVIEW,
  setChatLocation: (location) =>
    set((state) => {
      // Clear messages if switching chat locations
      if (state.chatLocation !== location) {
        return {
          chatLocation: location,
          chatMessages: null,
          activeRecordingId: null,
        };
      }
      return { chatLocation: location };
    }),

  activeRecordingId: null,
  setActiveRecordingId: (id) => set({ activeRecordingId: id }),

  clearChatForLocationSwitch: () =>
    set({
      chatMessages: null,
      initialUserMessageForChat: null,
    }),
}));

// ***** PDF VIEWER STATE ***** //
// ***** PDF VIEWER STATE ***** //
interface PDFViewerState {
  shouldShowPDFViewer: boolean;
  setShouldShowPDFViewer: (shouldShowPDFViewer: boolean) => void;

  pageToNavigateToInPDFViewer: number | null;
  setPageToNavigateToInPDFViewer: (page: number) => void;

  // Add persistent state for current page
  currentPDFPage: number;
  setCurrentPDFPage: (page: number) => void;

  // Track if PDF has been loaded at least once
  hasPDFBeenLoaded: boolean;
  setHasPDFBeenLoaded: (loaded: boolean) => void;
}

export const usePDFViewerStore = create<PDFViewerState>((set) => ({
  shouldShowPDFViewer: false,
  setShouldShowPDFViewer: (shouldShowPDFViewer: boolean) => {
    set({ shouldShowPDFViewer: shouldShowPDFViewer });
  },

  pageToNavigateToInPDFViewer: null,
  setPageToNavigateToInPDFViewer: (page: number | null) => set({ pageToNavigateToInPDFViewer: page }),

  currentPDFPage: 1,
  setCurrentPDFPage: (page: number) => set({ currentPDFPage: page }),

  hasPDFBeenLoaded: false,
  setHasPDFBeenLoaded: (loaded: boolean) => set({ hasPDFBeenLoaded: loaded }),
}));

// ***** STREAK STATE ***** //
type StreakState = {
  shouldShowStreakIncreasedPage: boolean;
  setShouldShowStreakIncreasedPage: (value: boolean) => void;

  currentStreakLength: number | null;
  setCurrentStreakLength: (value: number | null) => void;

  hasUserExtendedTheStreak: boolean;
  setHasUserExtendedTheStreak: (value: boolean) => void;
};

export const useStreakStore = create<StreakState>((set) => ({
  shouldShowStreakIncreasedPage: false,
  setShouldShowStreakIncreasedPage: (value: boolean) => set({ shouldShowStreakIncreasedPage: value }),

  currentStreakLength: null,
  setCurrentStreakLength: (value: number | null) => set({ currentStreakLength: value }),

  hasUserExtendedTheStreak: false,
  setHasUserExtendedTheStreak: (value: boolean) => set({ hasUserExtendedTheStreak: value }),
}));

type ExamState = {
  userLatestThreeExamData: ExamObject[];
  setUserLatestThreeExamData: (value: ExamObject[]) => void;
  selectedExam: ExamObject | null;
  setSelectedExam: (value: ExamObject | null) => void;
};

export const useExamStore = create<ExamState>((set) => ({
  selectedExam: null,
  setSelectedExam: (value: ExamObject | null) => set({ selectedExam: value }),
  userLatestThreeExamData: [],
  setUserLatestThreeExamData: (value: ExamObject[]) => set({ userLatestThreeExamData: value }),
}));

// ***** LEADERBOARD STATE ***** //
type LeaderboardState = {
  leaderboardRankingDataGroupByTimeAndViewFilter: {
    [timeFilterNameToUseForLeaderboard in TimeFilterNameToUseForLeaderboard]: {
      GLOBAL_FILTER_FOR_LEADERBOARD: LeaderboardData[];
      UNIVERSITY_NAME_FILTER_FOR_LEADERBOARD: LeaderboardData[];
    };
  };
  setLeaderboardRankingDataGroupByTimeAndViewFilter: (leaderboardRankingDataArray: any) => void;

  shouldShowLeaderboard: boolean;
  setShouldShowLeaderboard: (shouldShowLeaderboard: boolean) => void;
};

export const useLeaderboardStore = create<LeaderboardState>((set) => ({
  leaderboardRankingDataGroupByTimeAndViewFilter: {
    [TimeFilterNameToUseForLeaderboard.LEADERBOARD_ALL_TIME_FILTER_NAME]: {
      GLOBAL_FILTER_FOR_LEADERBOARD: [],
      UNIVERSITY_NAME_FILTER_FOR_LEADERBOARD: [],
    },
    [TimeFilterNameToUseForLeaderboard.LEADERBOARD_CURRENT_WEEK_FILTER_NAME]: {
      GLOBAL_FILTER_FOR_LEADERBOARD: [],
      UNIVERSITY_NAME_FILTER_FOR_LEADERBOARD: [],
    },
    [TimeFilterNameToUseForLeaderboard.LEADERBOARD_CURRENT_MONTH_FILTER_NAME]: {
      GLOBAL_FILTER_FOR_LEADERBOARD: [],
      UNIVERSITY_NAME_FILTER_FOR_LEADERBOARD: [],
    },
  },
  setLeaderboardRankingDataGroupByTimeAndViewFilter: (data) =>
    set({ leaderboardRankingDataGroupByTimeAndViewFilter: data }),

  shouldShowLeaderboard: true,
  setShouldShowLeaderboard: (shouldShowLeaderboard: boolean) => set({ shouldShowLeaderboard }),
}));

// ***** UPSELLS STATE ***** //
type ShowSubscriptionUpsellModalParams = {
  subscriptionPlanToShow?: UserSubscriptionPlan; // the subscription plan to show in the modal (optional — new two-step flow doesn't use it)
  contextForShowingSubscriptionUpsell: string; // the context in which the upsell is shown used for tracking
  reasonForUpsell?: ReasonForShowingSubscriptionUpsell; // the reason for showing the upsell
  shouldShowBackArrowButtonInModal?: boolean; // if true, modal shows a back arrow at the top
  // which plan card is highlighted by default on step 2 of the two-step flow (defaults to SUPER_LEARNER)
  defaultHighlightedPlanOnStepTwo?: UserSubscriptionPlan.SUPER_LEARNER | UserSubscriptionPlan.ELITE;
  // which step the modal opens on. Feature-specific upsells (step-1 templates,
  // image occlusion, the Pro→Elite dropdown item) skip the commitment page and
  // open directly on the plan picker. Defaults to "commit_to_subscribe".
  initialStep?: "commit_to_subscribe" | "plan_picker";
  // Shows this paywall format whatever the viewer's paywall-format assignment, and keeps the open out of the
  // paywall tests (paywall_format_2026_09 and paywall_social_proof_2026_09): no bucket is drawn, nothing is
  // recorded and no exposure is sent. The first upsell's version a2 opens the side-by-side screen this way from
  // its "see all plans" (owner, 2026-10-09).
  paywallFormatForcedOutsideExperiments?: PaywallFormatVariant;
};

type UpSellsState = {
  subscriptionPlanToShow: UserSubscriptionPlan | null;
  setSubscriptionPlanToShow: (subscriptionPlanToShow: UserSubscriptionPlan | null) => void;

  reasonForUpsell: ReasonForShowingSubscriptionUpsell | null;
  setReasonForUpsell: (reasonForUpsell: string | null) => void;

  contextForShowingSubscriptionUpsell: string | null;
  setContextForShowingSubscriptionUpsell: (contextForShowingSubscriptionUpsell: string | null) => void;

  shouldShowSubscriptionUpsellModal: boolean;
  hideSubscriptionUpsellModal: () => void;
  // See ShowSubscriptionUpsellModalParams.paywallFormatForcedOutsideExperiments; null when the open follows the tests
  paywallFormatForcedOutsideExperiments: PaywallFormatVariant | null;
  paywallTraceId: string | null;
  paywallReplayStartedAt: string | null;
  showSubscriptionUpsellModal: ({
    subscriptionPlanToShow,
    reasonForUpsell,
    contextForShowingSubscriptionUpsell,
    shouldShowBackArrowButtonInModal,
    defaultHighlightedPlanOnStepTwo,
    paywallFormatForcedOutsideExperiments,
  }: ShowSubscriptionUpsellModalParams) => Promise<void>;

  // UI flags for modal
  shouldShowBackArrowButtonInModal: boolean;
  setShouldShowBackArrowButtonInModal: (value: boolean) => void;

  // NEW — two-step modal state (spec §4.4)
  currentStepOfTwoStepUpsellModal: "commit_to_subscribe" | "plan_picker";
  setCurrentStepOfTwoStepUpsellModal: (step: "commit_to_subscribe" | "plan_picker") => void;
  // The step the modal was OPENED on (does not change as the user advances).
  // Drives the `upsell_view` analytics property: "plan_picker" → the user was
  // shown a specific feature upsell directly on the plan page;
  // "commit_to_subscribe" → the general two-step flow.
  initialStepOfTwoStepUpsellModal: "commit_to_subscribe" | "plan_picker";

  // NEW — question quality upsell modal (stacked layer above the two-step flow)
  isQuestionQualityUpsellModalOpen: boolean;
  entryPointForQuestionQualityUpsellModal:
    | "two_step_upsell_step_two"
    | "generation_options_page_floating_cta"
    | "voice_free_limit_reached_mid_call"
    | "voice_free_limit_blocked_pre_call"
    | null;
  openQuestionQualityUpsellModal: (
    entryPoint:
      | "two_step_upsell_step_two"
      | "generation_options_page_floating_cta"
      | "voice_free_limit_reached_mid_call"
      | "voice_free_limit_blocked_pre_call"
      | null
  ) => void;
  closeQuestionQualityUpsellModal: () => void;

  // NEW — "prove mastery for 20% off" voice challenge. Non-null while the
  // challenge modal is open; carries the concept + document being examined.
  // `language`: the deck/card language the examiner should speak in (resolved at
  // the open site) so non-English decks aren't quizzed in English. Falls back to
  // "English" when unresolved.
  masteryChallenge: { concept: string; deckId?: string; deckName?: string; language?: string } | null;
  openMasteryChallenge: (challenge: { concept: string; deckId?: string; deckName?: string; language?: string }) => void;
  closeMasteryChallenge: () => void;

  // NEW — which plan card is highlighted by default on step 2
  defaultHighlightedPlanOnStepTwo: UserSubscriptionPlan.SUPER_LEARNER | UserSubscriptionPlan.ELITE;
  setDefaultHighlightedPlanOnStepTwo: (plan: UserSubscriptionPlan.SUPER_LEARNER | UserSubscriptionPlan.ELITE) => void;

  // Which flow EliteFirstUpsellModalContainer chose when the modal opened (elite-first
  // sequence vs side-by-side), and the paywall-format stratum it was drawn in (null
  // outside the experiment). Fixed for the whole open so a country or profile update
  // can't switch screens mid-session; also stamped on funnel events. Null while closed.
  upsellFlowVariantOfOpenModal: UpsellFlowVariant | null;
  paywallFormatStratumOfOpenModal: PaywallFormatStratum | null;
  setUpsellFlowVariantOfOpenModal: (
    variant: UpsellFlowVariant | null,
    paywallFormatStratum?: PaywallFormatStratum | null
  ) => void;
};

export const useSubscriptionUpsellStore = create<UpSellsState>((set) => ({
  subscriptionPlanToShow: null,
  setSubscriptionPlanToShow: (subscriptionPlanToShow: UserSubscriptionPlan | null) => set({ subscriptionPlanToShow }),

  reasonForUpsell: null,
  setReasonForUpsell: (reasonForUpsell: ReasonForShowingSubscriptionUpsell | null) => set({ reasonForUpsell }),

  contextForShowingSubscriptionUpsell: null,
  setContextForShowingSubscriptionUpsell: (contextForShowingSubscriptionUpsell: string | null) =>
    set({ contextForShowingSubscriptionUpsell }),

  shouldShowSubscriptionUpsellModal: false,
  paywallFormatForcedOutsideExperiments: null,
  paywallTraceId: null,
  paywallReplayStartedAt: null,
  hideSubscriptionUpsellModal: () => {
    set({
      shouldShowSubscriptionUpsellModal: false,
      subscriptionPlanToShow: null,
      reasonForUpsell: null,
      contextForShowingSubscriptionUpsell: null,
      shouldShowBackArrowButtonInModal: false,
      paywallTraceId: null,
      paywallReplayStartedAt: null,
      // Reset two-step state so a stale step or open quality modal can't survive a close→reopen
      currentStepOfTwoStepUpsellModal: "commit_to_subscribe",
      initialStepOfTwoStepUpsellModal: "commit_to_subscribe",
      isQuestionQualityUpsellModalOpen: false,
      entryPointForQuestionQualityUpsellModal: null,
      defaultHighlightedPlanOnStepTwo: UserSubscriptionPlan.SUPER_LEARNER,
      upsellFlowVariantOfOpenModal: null,
      paywallFormatStratumOfOpenModal: null,
      paywallFormatForcedOutsideExperiments: null,
    });
  },
  showSubscriptionUpsellModal: async ({
    subscriptionPlanToShow,
    reasonForUpsell,
    contextForShowingSubscriptionUpsell,
    shouldShowBackArrowButtonInModal,
    defaultHighlightedPlanOnStepTwo,
    initialStep,
    paywallFormatForcedOutsideExperiments,
  }: ShowSubscriptionUpsellModalParams) => {
    const paywallTraceProperties = await startPaywallJourneyReplay({
      replayStartReason: "paywall_modal_trigger",
      context: contextForShowingSubscriptionUpsell,
      reasonForUpsell: reasonForUpsell ? String(reasonForUpsell) : undefined,
      subscriptionPlanToShow: subscriptionPlanToShow ? String(subscriptionPlanToShow) : undefined,
      paywallOpenedAt: new Date().toISOString(),
    });

    set({
      shouldShowSubscriptionUpsellModal: true,
      subscriptionPlanToShow: subscriptionPlanToShow ?? null,
      reasonForUpsell,
      contextForShowingSubscriptionUpsell,
      shouldShowBackArrowButtonInModal: !!shouldShowBackArrowButtonInModal,
      paywallTraceId: paywallTraceProperties.paywallTraceId || null,
      paywallReplayStartedAt: paywallTraceProperties.paywallReplayStartedAt || null,
      // Reset the step on every open: feature-specific upsells open directly on
      // the plan picker via initialStep; everything else starts on the commit page.
      currentStepOfTwoStepUpsellModal: initialStep ?? "commit_to_subscribe",
      initialStepOfTwoStepUpsellModal: initialStep ?? "commit_to_subscribe",
      isQuestionQualityUpsellModalOpen: false,
      entryPointForQuestionQualityUpsellModal: null,
      defaultHighlightedPlanOnStepTwo: defaultHighlightedPlanOnStepTwo ?? UserSubscriptionPlan.SUPER_LEARNER,
      paywallFormatForcedOutsideExperiments: paywallFormatForcedOutsideExperiments ?? null,
    });
  },

  shouldShowBackArrowButtonInModal: false,
  setShouldShowBackArrowButtonInModal: (value: boolean) => set({ shouldShowBackArrowButtonInModal: value }),

  // NEW — two-step modal state
  currentStepOfTwoStepUpsellModal: "commit_to_subscribe",
  setCurrentStepOfTwoStepUpsellModal: (step: "commit_to_subscribe" | "plan_picker") =>
    set({ currentStepOfTwoStepUpsellModal: step }),
  initialStepOfTwoStepUpsellModal: "commit_to_subscribe",

  // NEW — question quality upsell modal (stacked layer)
  isQuestionQualityUpsellModalOpen: false,
  entryPointForQuestionQualityUpsellModal: null,
  openQuestionQualityUpsellModal: (entryPoint) =>
    set({ isQuestionQualityUpsellModalOpen: true, entryPointForQuestionQualityUpsellModal: entryPoint }),
  closeQuestionQualityUpsellModal: () =>
    set({ isQuestionQualityUpsellModalOpen: false, entryPointForQuestionQualityUpsellModal: null }),
  masteryChallenge: null,
  openMasteryChallenge: (challenge) => set({ masteryChallenge: challenge }),
  closeMasteryChallenge: () => set({ masteryChallenge: null }),

  // NEW — default-highlighted plan card on step 2
  defaultHighlightedPlanOnStepTwo: UserSubscriptionPlan.SUPER_LEARNER,
  setDefaultHighlightedPlanOnStepTwo: (plan: UserSubscriptionPlan.SUPER_LEARNER | UserSubscriptionPlan.ELITE) =>
    set({ defaultHighlightedPlanOnStepTwo: plan }),

  upsellFlowVariantOfOpenModal: null,
  paywallFormatStratumOfOpenModal: null,
  setUpsellFlowVariantOfOpenModal: (variant: UpsellFlowVariant | null, paywallFormatStratum = null) =>
    set({ upsellFlowVariantOfOpenModal: variant, paywallFormatStratumOfOpenModal: paywallFormatStratum }),
}));

// ***** Global Webiste Store STATE ***** //
// used to store any miscellaneous global states that dont fit in the other stores
type GlobalWebsiteStoreState = {
  isAnyModalOpen: boolean;
  setIsAnyModalOpen: (isAnyModalOpen: boolean) => void;

  // Initialize isNavigationBarCollapsed based on window width
  isNavigationBarCollapsed: boolean;
  setIsNavigationBarCollapsed: (isCollapsed: boolean) => void;
  /** Set while the Tutor holds the sidebar collapsed; null otherwise. */
  navigationBarAutoCollapse: { collapsedStateToRestore: boolean; learnerChoseWhileHeld: boolean } | null;
  holdNavigationBarCollapsed: () => void;
  collapseNavigationBarForTutorCall: () => void;
  releaseNavigationBarCollapse: () => void;
  setIsNavigationBarCollapsedByLearner: (isCollapsed: boolean) => void;
  setIsNavigationBarCollapsedForViewport: (isTabletPortrait: boolean) => void;

  // Track when we're in sub-screens of generate questions page
  isInGenerateQuestionsSubScreen: boolean;
  setIsInGenerateQuestionsSubScreen: (isInSubScreen: boolean) => void;
};

export const useGlobalWebsiteStore = create<GlobalWebsiteStoreState>((set) => ({
  isAnyModalOpen: false,
  setIsAnyModalOpen: (isAnyModalOpen: boolean) => set({ isAnyModalOpen }),

  // Initialize isNavigationBarCollapsed based on window width
  isNavigationBarCollapsed:
    typeof window !== "undefined" &&
    window.innerWidth > MIN_WIDTH_OF_TABLET_PORTAIT_VIEW &&
    window.innerWidth <= MAX_WIDTH_OF_TABLET_PORTAIT_VIEW,
  setIsNavigationBarCollapsed: (isCollapsed: boolean) => set({ isNavigationBarCollapsed: isCollapsed }),
  // The Tutor needs the width, so it holds the bar collapsed while open and restores the earlier state on release.
  navigationBarAutoCollapse: null,
  holdNavigationBarCollapsed: () => set(state => state.navigationBarAutoCollapse ? state : {
    navigationBarAutoCollapse: { collapsedStateToRestore: state.isNavigationBarCollapsed, learnerChoseWhileHeld: false },
    isNavigationBarCollapsed: true }),
  // The card's call button is a new explicit action: it collapses even after the learner expanded the bar on Tutor.
  // It only changes an existing hold, so nothing is left held when no Tutor hook is active to release it.
  collapseNavigationBarForTutorCall: () => set(state => !state.navigationBarAutoCollapse ? state : {
    navigationBarAutoCollapse: { ...state.navigationBarAutoCollapse, learnerChoseWhileHeld: false },
    isNavigationBarCollapsed: true }),
  // A toggle the learner made on Tutor wins over the saved state.
  releaseNavigationBarCollapse: () => set(state => {
    const hold = state.navigationBarAutoCollapse;
    if (!hold) return state;
    return { navigationBarAutoCollapse: null,
      isNavigationBarCollapsed: hold.learnerChoseWhileHeld ? state.isNavigationBarCollapsed : hold.collapsedStateToRestore };
  }),
  setIsNavigationBarCollapsedByLearner: (isCollapsed: boolean) => set(state => ({ isNavigationBarCollapsed: isCollapsed,
    navigationBarAutoCollapse: state.navigationBarAutoCollapse && { ...state.navigationBarAutoCollapse, learnerChoseWhileHeld: true } })),
  // A resize resets the bar to "upright tablet?"; while held it updates only the state to restore.
  setIsNavigationBarCollapsedForViewport: (isTabletPortrait: boolean) => set(state => {
    const hold = state.navigationBarAutoCollapse;
    if (!hold) return { isNavigationBarCollapsed: isTabletPortrait };
    return { navigationBarAutoCollapse: { ...hold, collapsedStateToRestore: isTabletPortrait },
      isNavigationBarCollapsed: isTabletPortrait || (hold.learnerChoseWhileHeld ? state.isNavigationBarCollapsed : true) };
  }),

  // Track when we're in sub-screens of generate questions page
  isInGenerateQuestionsSubScreen: false,
  setIsInGenerateQuestionsSubScreen: (isInSubScreen: boolean) => set({ isInGenerateQuestionsSubScreen: isInSubScreen }),
}));

// ***** Gamification ***** //
// used to store any miscellaneous global states that dont fit in the other stores
type GamificationStoreState = {
  rewardToShow: Tree | "buddy";
  showRewardUnlockedModal: boolean;
  setShowRewardUnlockedModal: (showRewardUnlockedModal: boolean, rewardToShow: Tree | "buddy") => void;

  // river animation
  startRiverAnimationPercentage: number;
  setStartRiverAnimationPercentage: (startRiverAnimationPercentage: number) => void;
  endRiverAnimationPercentage: number;
  setEndRiverAnimationPercentage: (endRiverAnimationPercentage: number) => void;
  playRiverAnimationForTimeInMilliseconds: number | null;
  setPlayRiverAnimationForTimeInMilliseconds: (playRiverAnimationForTimeInMilliseconds: number | null) => void;

  // animated XP counter
  animatedXpEarnedAtCurrentLevel: number | null;
  setAnimatedXpEarnedAtCurrentLevel: (xp: number | null) => void;

  // animated level and progress info that updates immediately on level-up
  animatedCurrentLevel: number | null;
  setAnimatedCurrentLevel: (level: number | null) => void;
  animatedXpNeededToReachNextLevel: number | null;
  setAnimatedXpNeededToReachNextLevel: (xp: number | null) => void;
  animatedLevelsUntilNextRewardUnlock: number | null;
  setAnimatedLevelsUntilNextRewardUnlock: (levels: number | null) => void;
  animatedNameOfNextRewardToUnlock: NextRewardName | null;
  setAnimatedNameOfNextRewardToUnlock: (reward: NextRewardName | null) => void;

  // helper to reset all animated values
  resetAnimatedValues: () => void;
};

export const useGamificationStore = create<GamificationStoreState>((set) => ({
  rewardToShow: Tree.Kapok,
  showRewardUnlockedModal: false,
  setShowRewardUnlockedModal: (showRewardUnlockedModal: boolean, rewardToShow: Tree | "buddy") =>
    set({ showRewardUnlockedModal, rewardToShow }),

  playRiverAnimationForTimeInMilliseconds: null,
  setPlayRiverAnimationForTimeInMilliseconds: (playRiverAnimationForTimeInMilliseconds: number | null) =>
    set({ playRiverAnimationForTimeInMilliseconds }),

  startRiverAnimationPercentage: 0,
  setStartRiverAnimationPercentage: (startRiverAnimationPercentage: number) => set({ startRiverAnimationPercentage }),
  endRiverAnimationPercentage: 0,
  setEndRiverAnimationPercentage: (endRiverAnimationPercentage: number) => set({ endRiverAnimationPercentage }),

  // animated XP counter
  animatedXpEarnedAtCurrentLevel: null,
  setAnimatedXpEarnedAtCurrentLevel: (xp: number | null) => set({ animatedXpEarnedAtCurrentLevel: xp }),

  // animated level and progress info that updates immediately on level-up
  animatedCurrentLevel: null,
  setAnimatedCurrentLevel: (level: number | null) => set({ animatedCurrentLevel: level }),
  animatedXpNeededToReachNextLevel: null,
  setAnimatedXpNeededToReachNextLevel: (xp: number | null) => set({ animatedXpNeededToReachNextLevel: xp }),
  animatedLevelsUntilNextRewardUnlock: null,
  setAnimatedLevelsUntilNextRewardUnlock: (levels: number | null) =>
    set({ animatedLevelsUntilNextRewardUnlock: levels }),
  animatedNameOfNextRewardToUnlock: null,
  setAnimatedNameOfNextRewardToUnlock: (reward: NextRewardName | null) =>
    set({ animatedNameOfNextRewardToUnlock: reward }),

  // helper to reset all animated values
  resetAnimatedValues: () =>
    set({
      animatedXpEarnedAtCurrentLevel: null,
      animatedCurrentLevel: null,
      animatedXpNeededToReachNextLevel: null,
      animatedLevelsUntilNextRewardUnlock: null,
      animatedNameOfNextRewardToUnlock: null,
    }),
}));

// The review-checkpoint voice quiz's store lives in its own file (imported at
// the top); re-exported here so existing imports keep working.
export { useReviewCheckpointChallengeStore };
export type { ReviewCheckpointChallengePayload } from "./reviewCheckpointChallengeStore";

// Development-only: expose selected stores for easy manual testing in the browser console
if (import.meta.env.DEV && typeof window !== "undefined") {
  // Safe no-ops if reassigned; only for local manual QA
  (window as any).cardStore = cardStore;
  (window as any).useReviewDeckStore = useReviewDeckStore;
  // Lets local QA open the upsell / mastery-challenge modals directly, e.g.:
  //   useSubscriptionUpsellStore.getState().openMasteryChallenge({concept: "osmosis"})
  (window as any).useSubscriptionUpsellStore = useSubscriptionUpsellStore;
  // Lets local QA open the review-checkpoint quiz directly, e.g.:
  //   useReviewCheckpointChallengeStore.getState().openReviewCheckpointChallenge({concept:"osmosis", deckName:"Biology"})
  (window as any).useReviewCheckpointChallengeStore = useReviewCheckpointChallengeStore;
  // Lets local QA tweak the in-memory user (e.g. set the voice quota to
  // nearly-exhausted to rehearse the mid-call free-limit cutoff).
  (window as any).userStore = userStore;
}
